import React, {useState, useEffect, useRef, useCallback, useLayoutEffect, useReducer} from "react";
import TimeGrid from "../components/TimeGrid";
import AnnotationLayer from "../components/AnnotationLayer";
import AnnotationForm from "../components/AnnotationForm";
import InstrumentMenu, {getDynamicIcon, InstrumentMenuOptions} from "../components/InstrumentMenu";
import BarSettingsBatchEditor from "../components/BarSettingsBatchEditor";
import TimeSignatureSelector from "../components/TimeSignatureSelector";
import LoadingSpinner from "../components/LoadingSpinner";
import {FaChevronLeft, FaSave, FaUpload, FaDownload} from 'react-icons/fa';
import FilePickerButton from "../components/FilePickerButton";
import NeuroMuseLogo from '../assets/img/logo/NeuroMuse_Logo_NoText.svg';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoTooltip from "../components/InfoTooltip";
import { authFetch } from "../utils/authFetch";

// Import custom hooks
import {useTimeSignatures} from "../hooks/useTimeSignatures";
import {useTracks} from "../hooks/useTracks";
import {useZoom} from "../hooks/useZoom";

// Import utils
import {calculatePosition} from "../utils/annotationUtils";
import {ZOOM_RANGE, COLORS, isUnknownTrack} from "../utils/constants";
import {
	Typography,
	Button,
	Box,
	Collapse,
	Popover,
	Menu,
	MenuItem,
	DialogTitle,
	DialogContent,
	DialogContentText,
	Dialog,
	DialogActions,
	Tooltip,
	FormControlLabel,
	Switch
} from "@mui/material";
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactComponent as EditIcon } from "../assets/img/tool_icons/Edit.svg";
import { ReactComponent as ViewIcon } from "../assets/img/tool_icons/View.svg";
import {useAnnotation} from '../contexts/AnnotationContext';
import {SongLevelAttributes} from "../lib/definitions/SongLevelAnnotation";
import {useParams, useNavigate, useLocation} from "react-router-dom";
import {useCustomAuth} from "../contexts/AuthContext";
import MultipleChoice from "../components/MultipleChoice";
import {ChordLabellingSystemOptions, ChordLabellingSystemInstruction} from "../lib/definitions/ChordTrackAnnotation";
import annotationDataService from "../services/AnnotationDataService";
import {BsTrash} from "react-icons/bs";
import {AWS_LAMBDA_ENDPOINT} from "../utils/constants";
import {DndContext, closestCenter, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import SortableTrack from '../components/SortableTrack';
import { uploadMidi } from "../utils/uploadMidi";
import { useViewMode } from "../contexts/ViewModeContext";
import {useAuth} from "react-oidc-context";
import {UserProfile} from "../components/UserProfile";
import Playhead from "../components/Playhead";
import {SimCardDownloadOutlined} from "@mui/icons-material";
import { useShortcutHandler } from "../hooks/useShortcutHandler";
import ContextMenuWrapper from "../components/ContextMenuWrapper";
import {BarLevelAnnotationInstruction} from "../lib/definitions/SongLevelAnnotation"
import {toMusicalPositionWithGridLevel} from "../utils/annotationUtils";
import ErrorValidationDialog from "../components/ErrorValidationDialog";
import TrackDeletionConfirmDialog from "../components/TrackDeletionConfirmDialog";
import InstrumentValidationErrorDialog from "../components/InstrumentValidationErrorDialog";
import InstrumentChangeConfirmDialog from "../components/InstrumentChangeConfirmDialog";
import AnnotationSheetAccessDialog from "../components/AnnotationSheetAccessDialog";
import AnnotationToolToggle from "../components/AnnotationToolToggle";
import AnnotationClipboardMenu from "../components/AnnotationClipboardMenu";
import AnnotationPasteDialog from "../components/AnnotationPasteDialog";
import AnnotationBatchDeleteDialog from "../components/AnnotationBatchDeleteDialog";
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import {
	annotationEditorReducer,
	createAnnotationEditorState,
	createClipboard,
	getClipboardPresentation,
	getCanPaste,
	getContextMenuSelectionAction,
} from "../state/annotationEditorInteraction";
import { planPaste, selectAnnotationRange } from "../utils/annotationGroupOperations";
import { intervalsOverlap } from "../utils/annotationCollision";
import { createAnnotationBlockDefaults } from "../utils/annotationBlockDefaults";
import { preservePastedBlockCompletion } from "../utils/annotationClipboardCopy";
import {
	getAnchoredTimelineScrollLeft,
	getHorizontalTimelineDelta,
	getPlayheadRevealScroll,
	getTimelineGestureZoomLevel,
	getTimelineKeyboardZoomIntent,
	getTimelineWheelZoomIntent,
} from "../utils/annotationTimelineScroll";
import {
	canUseEditorActions,
	getAccessNoticeDismissLocation,
	getInitialRequestedMode,
	getSaveAlertMessage,
	requestModeChange,
} from "./annotationSheetModeActions";

const annotationInterval = annotation => ({
	start: [annotation.startMeasure, annotation.startBeat, annotation.startSubdivision || 0],
	end: [annotation.endMeasure, annotation.endBeat, annotation.endSubdivision || 0],
});

const positionPatch = interval => ({
	startMeasure: interval.start[0],
	startBeat: interval.start[1],
	startSubdivision: interval.start[2],
	endMeasure: interval.end[0],
	endBeat: interval.end[1],
	endSubdivision: interval.end[2],
});

const isTypingElement = element => Boolean(element && (
	element.tagName === 'INPUT' ||
	element.tagName === 'TEXTAREA' ||
	element.tagName === 'SELECT' ||
	element.contentEditable === 'true'
));

/**
 * App component - implementation with per-measure time signatures
 */

function AnnotationSheet({handleLogout}) {
	// Number of measures
	const [expandedSection, setExpandedSection] = useState("song");
	const [clearOrDeleteTrackAnchorEl, setClearOrDeleteTrackAnchorEl] = useState(null);
	const [clearOrDeleteTrackId, setClearOrDeleteTrackId] = useState('');
	const [trackDeletionDialogOpen, setTrackDeletionDialogOpen] = useState(false);
	const [trackToToggleDeletion, setTrackToToggleDeletion] = useState(null);
	const [sharedScrollLeft, setSharedScrollLeft] = useState(0);
	const [loading, setLoading] = useState(false);
	const [unAppliedChanges, setUnAppliedChanges] = useState(false);
	const [sharedGridLevel, setSharedGridLevel] = useState('bar');
	const [submitModalOpen, setSubmitModalOpen] = useState(false);

	// Time out for auto saving general information form
	const debounceRef = useRef(null);
	const sharedScrollContainerRef = useRef(null);
	const pendingHorizontalZoomAnchorRef = useRef(null);
	const gestureZoomRef = useRef(null);
	const [sharedScrollContainer, setSharedScrollContainer] = useState(null);
	const [timelineInteractionElement, setTimelineInteractionElement] = useState(null);
	const attachSharedScrollContainer = useCallback(node => {
		sharedScrollContainerRef.current = node;
		setSharedScrollContainer(node);
	}, []);
	const attachTimelineInteractionElement = useCallback(node => {
		setTimelineInteractionElement(node);
	}, []);
	const batchEditorRef = useRef();

	// maintains previous folder path
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const rawPath = queryParams.get("path") ?? "";
	const originalPathArray = rawPath === "" ? [] : rawPath.split("/");

	const {sheetId, view} = useParams();
	const {userData, isAdminMode} = useCustomAuth();
	const userId = userData?.user_id;
	const auth = useAuth();
	const userName = auth.user?.profile?.name || "User";
	const [downloadLoading, setDownloadLoading] = useState(false);
	const [uploadLoading, setUploadLoading] = useState(false);
	const isAdmin = userData?.roles.includes("ADMIN") || userData?.roles.includes("ROOT") || false;

	// maintains playhead stuff
	const [playheadX, setPlayheadX] = useState(0);
	const [isPlayheadDragging, setIsPlayheadDragging] = useState(false);
	const scrollIntervalRef = useRef(null);
	const scrollDirectionRef = useRef(null);
	const scrollSpeedRef = useRef(1);

	const navigate = useNavigate();

	// View/edit mode. The server-confirmed lease is the source of truth; the URL is
	// only the requested initial mode and is updated after successful transitions.
	const {
		sessionId,
		mode,
		isViewMode,
		isTrackEditMode,
		accessReady,
		accessHealthy,
		isTransitioning,
		notice: accessNotice,
		acquireAccess,
		switchToViewing,
		switchToEditing,
		switchToTrackEditing,
		clearNotice: clearAccessNotice,
	} = useViewMode();
	const accessRequestStartedRef = useRef(null);
	const initializedSheetRef = useRef(null);
	const editorActionsEnabled = canUseEditorActions({mode, accessHealthy});
	const [editorInteraction, dispatchEditorInteraction] = useReducer(
		annotationEditorReducer,
		undefined,
		createAnnotationEditorState
	);
	const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
	const [pasteError, setPasteError] = useState(null);
	const [pasteTrackId, setPasteTrackId] = useState(null);
	const [pasteTargetPulseKey, setPasteTargetPulseKey] = useState(0);
	const [deleteDialogSelection, setDeleteDialogSelection] = useState(null);

	const [anchorEl, setAnchorEl] = useState(null);

	const handleMenuOpen = (e) => {
	  setAnchorEl(e.currentTarget);
	};
	const handleMenuClose = () => {
	  setAnchorEl(null);
	};
	const handleModeChange = useCallback(async (targetMode) => {
		const changed = await requestModeChange(targetMode, {
			switchToViewing,
			switchToEditing,
			switchToTrackEditing,
		});
		if (!changed) return false;

		const routeMode = targetMode === "view" ? "view" : "edit";
		navigate(`/annotation/${sheetId}/${routeMode}${location.search}`, {replace: true});
		setAnchorEl(null);
		return true;
	}, [location.search, navigate, sheetId, switchToEditing, switchToTrackEditing, switchToViewing]);

	const {
		annotations,
		initializeSheet,
		selectedAnnotationId,
		isFormOpen,
		generalInfo,
		handleSongGeneralInfoUpdate,
		keyChanges,
		handleKeyChangeAdd,
		handleKeyChangeUpdate,
		handleKeyChangeDelete,
		tempoChanges,
		handleTempoChangeAdd,
		handleTempoChangeUpdate,
		handleTempoChangeDelete,
		handleAnnotationCreate,
		handleAnnotationsCreate,
		handleAnnotationUpdate,
		handleAnnotationSelect,
		handleAnnotationDelete,
		handleAnnotationsDelete,
		handleAnnotationResize,
		handleAnnotationsMove,
		getTrackAnnotations,
		saveAnnotationSheet,
		error,
		isInitialized,
		instrumentValidationError,
	} = useAnnotation();

	// Use custom hooks
	const {
		timeSignatures,
		beatsPerMeasureMap,
		showTimeSignatureSelector,
		activeMeasure,
		selectorPosition,
		handleTimeSignatureClick,
		handleTimeSignatureChange,
		handleBatchTimeSignatureChanges,
		handleCloseSelector
	} = useTimeSignatures();

	// Pass the number of bars and beats per measure map to useZoom hook
	const {
		horizontalZoomLevel,
		dynamicZoomValue,
		verticalZoomLevel,
		handleHorizontalZoomChange,
		handleVerticalZoomChange,
		setHorizontalZoomLevelValue,
		setVerticalZoomLevelValue,
		adjustHorizontalZoom,
		adjustVerticalZoom,
	} = useZoom(generalInfo?.duration_number_of_bars || 16, beatsPerMeasureMap);
	const sheetMeasureCount = generalInfo?.duration_number_of_bars || 16;

	const prevZoomRef = useRef(dynamicZoomValue);
	const getTimelinePointerPosition = useCallback(clientX => {
		const container = sharedScrollContainerRef.current;
		if (!container || !Number.isFinite(clientX)) return null;
		const bounds = container.getBoundingClientRect();
		if (clientX < bounds.left || clientX > bounds.right) return null;
		const viewportX = Math.min(Math.max(clientX - bounds.left, 0), container.clientWidth);
		return {
			container,
			viewportX,
			contentX: container.scrollLeft + viewportX,
		};
	}, []);

	const captureHorizontalZoomAnchor = useCallback(clientX => {
		const container = sharedScrollContainerRef.current;
		if (!container) return false;
		let position = getTimelinePointerPosition(clientX);
		if (Number.isFinite(clientX) && !position) return false;
		if (!position) {
			const playheadViewportX = playheadX - container.scrollLeft;
			const viewportX = playheadViewportX >= 0 && playheadViewportX <= container.clientWidth
				? playheadViewportX
				: container.clientWidth / 2;
			position = {
				viewportX,
				contentX: container.scrollLeft + viewportX,
			};
		}
		pendingHorizontalZoomAnchorRef.current = {
			contentX: position.contentX,
			viewportX: position.viewportX,
		};
		return true;
	}, [getTimelinePointerPosition, playheadX]);

	useLayoutEffect(() => {
		if (!sharedScrollContainerRef.current || !prevZoomRef.current) return;

		const container = sharedScrollContainerRef.current;
		const prevZoom = prevZoomRef.current;
		const newZoom = dynamicZoomValue;

		if (prevZoom !== newZoom) {
			const scaleRatio = newZoom / prevZoom;
			const zoomAnchor = pendingHorizontalZoomAnchorRef.current;
			pendingHorizontalZoomAnchorRef.current = null;
			const previousPlayheadX = playheadX;

			// Keep the ruler, tracks, playhead, and scroll anchor in the same
			// layout pass. Deferring these updates leaves Safari one frame where
			// the timeline sections are rendered at different zoom levels.
			prevZoomRef.current = newZoom;
			setPlayheadX(previous => previous * scaleRatio);
			let nextScrollLeft;
			if (zoomAnchor) {
				nextScrollLeft = getAnchoredTimelineScrollLeft({
					...zoomAnchor,
					scaleRatio,
					clientWidth: container.clientWidth,
					scrollWidth: container.scrollWidth,
				});
			} else {
				const deltaX = previousPlayheadX * scaleRatio - previousPlayheadX;
				const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
				nextScrollLeft = Math.min(Math.max(container.scrollLeft + deltaX, 0), maxScrollLeft);
			}
			container.scrollLeft = nextScrollLeft;
			setSharedScrollLeft(nextScrollLeft);
		}

		prevZoomRef.current = dynamicZoomValue;
	}, [dynamicZoomValue, playheadX]);

	useEffect(() => {
		if (!userId || accessRequestStartedRef.current === sheetId) return;
		accessRequestStartedRef.current = sheetId;
		let cancelled = false;

		const initializeWithAccess = async () => {
			const requestedMode = getInitialRequestedMode({
				routeMode: view,
				isAdmin,
				isAdminMode,
			});
			const acquired = await acquireAccess(sheetId, requestedMode);
			if (!acquired || cancelled) {
				if (!acquired) accessRequestStartedRef.current = null;
				return;
			}
			await initializeSheet(sheetId, userId, sessionId);
			if (!cancelled) initializedSheetRef.current = sheetId;
		};

		initializeWithAccess();
		return () => {
			cancelled = true;
			if (
				initializedSheetRef.current !== sheetId &&
				accessRequestStartedRef.current === sheetId
			) {
				accessRequestStartedRef.current = null;
			}
		};
	}, [acquireAccess, initializeSheet, isAdmin, isAdminMode, sessionId, sheetId, userId, view]);

	// Autosave functionality - now safe to use with UI_DEVELOPMENT_MODE
	useEffect(() => {
		if (!isInitialized || mode !== "edit" || !accessHealthy) return;

		const interval = setInterval(async () => {
			try {
				// console.log("Autosave at ", new Date().toLocaleString());
				await handleSave(true);
			} catch (err) {
				console.error("Autosave failed:", err);
			}
		}, 10000); // 10s

		return () => clearInterval(interval);
	}, [accessHealthy, isInitialized, mode]);

  // Disable left scroll to go back to previous page
	const sharedScrollRef = useRef(null);

	useEffect(() => {
		for (let el of [document.documentElement, document.body]) {
		  el.style.overscrollBehaviorX = "none";
		  el.style.touchAction        = "pan-y";
		}
	  }, []);

	const {
		tracks,
		selectedTrackId,
		handleTrackSelect,
		addNewTrack,
		deleteTrack,
		toggleTrackCollapse,
		chordLabellingSystem,
		unlockChordTrack,
		lockChordTrack,
		editTrack,
		reorderTracks,
		toggleMainMelodyTrack,
		toggleTrackForDeletion,
		instrumentChangeDialog,
		handleInstrumentChangeConfirm,
		handleInstrumentChangeCancel
	} = useTracks();

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
			  distance: 5  // only start drag after 5px of movement
			}
		})
	);
	const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

	// Handle drag end event to reorder tracks
	const handleDragEnd = (event) => {
		// Only allow track reordering in Track Edit Mode
		if (!isTrackEditMode) return;

		const {active, over} = event;

		if (!over || active.id === over.id) return;

		let newIndex = tracks.findIndex(t => t.id === over.id);

		// disallow dropping over music structure and chord tracks
		newIndex = Math.max(newIndex, 2);
		const newId = tracks[newIndex].id;
		reorderTracks(active.id, newId);
	};

	// Make tracks available globally for annotation creation
	useEffect(() => {
		// Expose tracks data to the window for access in AnnotationLayer
		window.trackData = tracks;

		return () => {
			// Clean up when component unmounts
			delete window.trackData;
		};
	}, [tracks]);

	// Remove any existing timeouts
	useEffect(() => {
		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		};
	}, []);

	const handleDownloadMidi = async () => {
		setDownloadLoading(true);
		try {
		  const res = await authFetch(`${AWS_LAMBDA_ENDPOINT}/downloadMidi`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ sheet_id: sheetId }) // defaults to "processed" on the backend
		  });
	  
		  if (!res.ok) throw new Error(`HTTP ${res.status}`);
		  const { urls } = await res.json();
		  if (!urls || !urls.length) throw new Error("No URL returned");
		  
		  const { url } = urls[0];
	  
		  // open the presigned URL in a new tab (triggers download)
		  window.open(url, "_blank");
		  alert("MIDI file download started!");
		} catch (err) {
		  console.error("Download failed:", err);
		  alert("Could not download MIDI file.");
		} finally {
		  setDownloadLoading(false);
		}
	  };
	  

	// Handle general info form change
	const handleSongGeneralInfoChange = (newGeneralInfo) => {
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}
		debounceRef.current = setTimeout(() => {
			handleSongGeneralInfoUpdate(newGeneralInfo);
		}, 5000);
	};

	const clipboardSelection = editorInteraction.selection;

	useEffect(() => {
		dispatchEditorInteraction({ type: 'RESET_SHEET' });
		setPasteDialogOpen(false);
		setPasteError(null);
		setPasteTrackId(null);
		setDeleteDialogSelection(null);
	}, [sessionId, sheetId]);

	useEffect(() => {
		if (!isViewMode && !isTrackEditMode) return;
		dispatchEditorInteraction({ type: 'DISMISS_TRANSIENT' });
		setPasteDialogOpen(false);
		setPasteError(null);
		setPasteTrackId(null);
		setDeleteDialogSelection(null);
	}, [isTrackEditMode, isViewMode]);

	const handleSelectionChange = useCallback(selection => {
		dispatchEditorInteraction({ type: 'REPLACE_SELECTION', ...selection });
	}, []);

	const handleBlockSelectionRequest = useCallback(({ trackId, annotationId, shiftKey, toggleKey }) => {
		if (editorInteraction.toolMode !== 'cursor' || isViewMode || isTrackEditMode) return;
		if (
			shiftKey &&
			clipboardSelection.trackId === trackId &&
			clipboardSelection.anchorId
		) {
			const ids = selectAnnotationRange({
				annotations: getTrackAnnotations(trackId),
				trackId,
				anchorId: clipboardSelection.anchorId,
				targetId: annotationId,
			});
			dispatchEditorInteraction({
				type: 'REPLACE_SELECTION',
				trackId,
				ids,
				primaryId: annotationId,
				anchorId: clipboardSelection.anchorId,
			});
			return;
		}
		if (toggleKey) {
			dispatchEditorInteraction({ type: 'TOGGLE_SELECTION', trackId, id: annotationId });
			return;
		}
		dispatchEditorInteraction({
			type: 'REPLACE_SELECTION',
			trackId,
			ids: [annotationId],
			primaryId: annotationId,
			anchorId: annotationId,
		});
	}, [clipboardSelection, editorInteraction.toolMode, getTrackAnnotations, isTrackEditMode, isViewMode]);

	const handleSelectionGeometry = useCallback(({ trackId, position }) => {
		if (editorInteraction.contextMenu) return;
		if (clipboardSelection.trackId !== trackId || clipboardSelection.ids.length === 0) return;
		dispatchEditorInteraction({ type: 'SET_COPY_POPOVER', position });
	}, [clipboardSelection.ids.length, clipboardSelection.trackId, editorInteraction.contextMenu]);

	const handleAnnotationDeleteWithSelection = useCallback(annotationId => {
		const deleted = handleAnnotationDelete(annotationId);
		if (deleted) {
			dispatchEditorInteraction({ type: 'REMOVE_SELECTED_ID', id: annotationId });
		}
		return deleted;
	}, [handleAnnotationDelete]);

	const handleCopy = useCallback(() => {
		if (
			isViewMode ||
			isTrackEditMode ||
			editorInteraction.toolMode !== 'cursor' ||
			!clipboardSelection.trackId ||
			clipboardSelection.ids.length === 0
		) return;
		const selectedIdSet = new Set(clipboardSelection.ids);
		const blocks = getTrackAnnotations(clipboardSelection.trackId)
			.filter(annotation => selectedIdSet.has(annotation.id));
		if (blocks.length !== clipboardSelection.ids.length) return;
		const clipboard = createClipboard({
			sheetId,
			sessionId,
			trackId: clipboardSelection.trackId,
			blocks,
		});
		dispatchEditorInteraction({
			type: 'COPY_SELECTION',
			clipboard,
			sourceIds: blocks.map(block => block.id),
		});
	}, [clipboardSelection, editorInteraction.toolMode, getTrackAnnotations, isTrackEditMode, isViewMode, sessionId, sheetId]);

	const revealPasteTarget = useCallback(() => {
		const container = sharedScrollContainerRef.current;
		if (container) {
			const nextScrollLeft = getPlayheadRevealScroll({
				playheadX,
				scrollLeft: container.scrollLeft,
				clientWidth: container.clientWidth,
				scrollWidth: container.scrollWidth,
			});
			if (nextScrollLeft !== null) {
				container.scrollLeft = nextScrollLeft;
				setSharedScrollLeft(nextScrollLeft);
			}
		}
		setPasteTargetPulseKey(key => key + 1);
	}, [playheadX]);

	const planPasteAtPlayhead = useCallback(trackId => {
		const actualPixelsPerBeat = 40 * dynamicZoomValue;
		const toValue = position => calculatePosition(
			position[0], position[1], position[2],
			beatsPerMeasureMap, actualPixelsPerBeat, timeSignatures
		);
		const fromValue = value => {
			const converted = toMusicalPositionWithGridLevel(
				value, sharedGridLevel, false,
				beatsPerMeasureMap, actualPixelsPerBeat, timeSignatures
			);
			return [converted.measure, converted.beat, converted.subdivision || 0];
		};
		const sheetEnd = calculatePosition(
			sheetMeasureCount + 1, 1, 1,
			beatsPerMeasureMap, actualPixelsPerBeat, timeSignatures
		);
		return planPaste({
			clipboard: editorInteraction.clipboard,
			sheetId,
			sessionId,
			trackId,
			playheadValue: playheadX,
			sheetEnd,
			annotations: getTrackAnnotations(trackId),
			toValue,
			fromValue,
		});
	}, [
		beatsPerMeasureMap, dynamicZoomValue, editorInteraction.clipboard,
		getTrackAnnotations, playheadX, sessionId, sharedGridLevel, sheetId,
		sheetMeasureCount, timeSignatures,
	]);

	const openPasteDialog = useCallback(trackId => {
		if (!getCanPaste(editorInteraction, trackId) || isViewMode || isTrackEditMode) return;
		revealPasteTarget();
		setPasteTrackId(trackId);
		const plan = planPasteAtPlayhead(trackId);
		setPasteError(plan.success ? null : plan.reason);
		setPasteDialogOpen(true);
		dispatchEditorInteraction({ type: 'CLOSE_CONTEXT_MENU' });
	}, [editorInteraction, isTrackEditMode, isViewMode, planPasteAtPlayhead, revealPasteTarget]);

	const handleClipboardContextMenu = useCallback(({ trackId, annotationId, position }) => {
		if (editorInteraction.toolMode !== 'cursor' || isViewMode || isTrackEditMode) return;
		const selectionAction = getContextMenuSelectionAction({
			selection: clipboardSelection,
			trackId,
			annotationId,
		});
		if (selectionAction) dispatchEditorInteraction(selectionAction);
		dispatchEditorInteraction({
			type: 'OPEN_CONTEXT_MENU',
			menu: { trackId, position },
		});
	}, [clipboardSelection, editorInteraction.toolMode, isTrackEditMode, isViewMode]);

	const openDeleteDialog = useCallback(() => {
		const trackId = editorInteraction.contextMenu?.trackId;
		if (
			isViewMode ||
			isTrackEditMode ||
			editorInteraction.toolMode !== 'cursor' ||
			!trackId ||
			clipboardSelection.trackId !== trackId ||
			clipboardSelection.ids.length === 0
		) return;

		setDeleteDialogSelection({
			trackId,
			ids: [...clipboardSelection.ids],
		});
		dispatchEditorInteraction({ type: 'CLOSE_CONTEXT_MENU' });
	}, [
		clipboardSelection.ids,
		clipboardSelection.trackId,
		editorInteraction.contextMenu?.trackId,
		editorInteraction.toolMode,
		isTrackEditMode,
		isViewMode,
	]);

	const confirmDeleteSelection = useCallback(() => {
		if (!deleteDialogSelection?.ids.length) return;
		const result = handleAnnotationsDelete(deleteDialogSelection.ids);
		if (result.success) {
			dispatchEditorInteraction({
				type: 'DELETE_SELECTION_SUCCEEDED',
				ids: deleteDialogSelection.ids,
			});
		}
		setDeleteDialogSelection(null);
	}, [deleteDialogSelection, handleAnnotationsDelete]);

	const handlePasteChoice = useCallback(choice => {
		const trackId = pasteTrackId;
		const plan = planPasteAtPlayhead(trackId);
		if (!plan.success) {
			setPasteError(plan.reason);
			return;
		}

		const trackInfo = tracks.find(track => track.id === trackId);
		const defaults = createAnnotationBlockDefaults({
			trackId,
			trackInfo,
			trackAnnotations: getTrackAnnotations(trackId),
		});
		const blocks = plan.intervals.map(({ source, interval }) => {
			const { id, ...sourceWithoutId } = source;
			return preservePastedBlockCompletion({
				...sourceWithoutId,
				trackId,
				...positionPatch(interval),
				...(choice === 'blocks-only' ? defaults : {}),
			});
		});
		const result = handleAnnotationsCreate(blocks, {
			validate: ({ proposedBlocks, trackBlocks }) => {
				const proposedIntervals = proposedBlocks.map(annotationInterval);
				const collidesWithExisting = proposedIntervals.some(interval => (
					trackBlocks.some(block => intervalsOverlap(interval, annotationInterval(block)))
				));
				const collidesInternally = proposedIntervals.some((interval, index) => (
					proposedIntervals.slice(index + 1).some(other => intervalsOverlap(interval, other))
				));
				return collidesWithExisting || collidesInternally
					? { success: false, reason: 'collision' }
					: true;
			},
		});
		if (!result.success) {
			setPasteError(result.reason || 'invalid-clipboard');
			return;
		}
		dispatchEditorInteraction({
			type: 'PASTE_SUCCEEDED',
			trackId,
			ids: result.blocks.map(block => block.id),
		});
		setPasteDialogOpen(false);
		setPasteError(null);
	}, [
		getTrackAnnotations, handleAnnotationsCreate, pasteTrackId, planPasteAtPlayhead, tracks,
	]);

	useEffect(() => {
		const handleClipboardShortcut = event => {
			if (
				isViewMode ||
				isTrackEditMode ||
				editorInteraction.toolMode !== 'cursor' ||
				isFormOpen ||
				pasteDialogOpen ||
				Boolean(deleteDialogSelection) ||
				isTypingElement(document.activeElement) ||
				!(event.metaKey || event.ctrlKey)
			) return;
			const key = event.key.toLowerCase();
			if (key === 'c' && clipboardSelection.ids.length > 0) {
				event.preventDefault();
				handleCopy();
			}
			if (key === 'v' && editorInteraction.clipboard?.trackId) {
				event.preventDefault();
				openPasteDialog(editorInteraction.clipboard.trackId);
			}
		};
		window.addEventListener('keydown', handleClipboardShortcut);
		return () => window.removeEventListener('keydown', handleClipboardShortcut);
	}, [
		clipboardSelection.ids.length, editorInteraction.clipboard?.trackId,
		editorInteraction.toolMode, handleCopy, isFormOpen, isTrackEditMode, isViewMode,
		deleteDialogSelection, openPasteDialog, pasteDialogOpen,
	]);

	// Keyboard shortcuts for view modes
	useEffect(() => {
		const handleKeyDown = (e) => {
			// Only handle shortcuts in admin mode
			if (!isAdmin || !isAdminMode) return;
			
			// Don't handle shortcuts if a form is open or user is typing in an input
			const activeElement = document.activeElement;
			if (activeElement && (
				activeElement.tagName === 'INPUT' || 
				activeElement.tagName === 'TEXTAREA' || 
				activeElement.contentEditable === 'true'
			)) return;

			const key = e.key.toLowerCase();
			
			// Handle single key shortcuts (no modifiers)
			if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
				switch (key) {
					case 'v':
						// Switch to View mode
						e.preventDefault();
						handleModeChange("view");
						break;
					case 'e':
						// Switch to Edit mode (Track Edit disabled)
						e.preventDefault();
						handleModeChange("edit");
						break;
				}
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [handleModeChange, isAdmin, isAdminMode]);

	const handleClearOrDeleteTrackPopoverOpen = (event, trackId) => {
		setClearOrDeleteTrackAnchorEl(event.currentTarget);
		setClearOrDeleteTrackId(trackId);
	};

	const handleClearOrDeleteTrackPopoverClose = () => {
		setClearOrDeleteTrackAnchorEl(null);
		setClearOrDeleteTrackId('');
	};

	const clearTrack = (trackId) => {
		const trackAnnotations = getTrackAnnotations(trackId);
		for (const annotation of trackAnnotations) {
			handleAnnotationDelete(annotation.id);
		}
		if (trackId === 'chord-track') {
			lockChordTrack(trackId);
		}
		annotationDataService.notifyListeners();
	}

	const clearOrRemoveTrack = (clear) => {
		// console.log(clearOrDeleteTrackId, clear);
		if (clear) {
			clearTrack(clearOrDeleteTrackId);
		} else {
			deleteTrack(clearOrDeleteTrackId);
		}
		handleClearOrDeleteTrackPopoverClose();
		annotationDataService.notifyListeners();
	}

	// internal handler
	const handleClickSave = async () => {
		if (!editorActionsEnabled) return;
		if (unAppliedChanges) {
			// open dialog if there's unsaved change
			setShowUnsavedDialog(true);
		} else {
			await handleSave(false);
		}
	};

	const [alertOpen, setAlertOpen] = useState(false);
	const [validationErrors, setValidationErrors] = useState([]);

	const handleSubmit = async () => {
		if (!editorActionsEnabled) return;
		// console.log('Starting annotation sheet submission...');

		const songLevelComplete = generalInfo?.completed;
		const durationComplete = batchEditorRef.current?.getDurationValue() > 0 ? true : false;
		const timeSignatureComplete = batchEditorRef.current?.getTimeSignature().length > 0 ? true : false;
		const keyComplete = batchEditorRef.current?.getKey().length > 0 ? true : false;
		const tempoComplete = batchEditorRef.current?.getTempo().length > 0 ? true : false;
		const musicalScoreApplied = !batchEditorRef.current?.getIsDirty();
		let musicStructureTrackHasBlock = false;
		let chordTrackHasBlock = false;
		let hasValidInstrumentTrack = false;
		let hasUnknownTrack = false;
		const instrumentTracksMissingBlock = [];
		const incompleteBlocks = [];
		const errors = [];
		const mainMelodyTracks = [];

		for (const track of tracks) {
			const trackAnnotations = getTrackAnnotations(track.id);

			if (track.id == "music-structure-track") {
				musicStructureTrackHasBlock = trackAnnotations.length > 0;
			}
			else if (track.id == "chord-track") {
				chordTrackHasBlock = trackAnnotations.length > 0;
			}
			else if (track.type == "instrument") {
				// Check there is at least one instrumental track left (i.e., NOT marked for deletion)
				if (track.marked_for_deletion === false) {
					hasValidInstrumentTrack = true;
					// Check there is no "unknown" instrumental track
					if (track.name?.toLowerCase() === "unknown") {
						hasUnknownTrack = true;
					}
					// Check all valid instrumental tracks contain at least 1 block
					if (trackAnnotations.length === 0) {
						instrumentTracksMissingBlock.push(track);
					}
					// Extract all instrumental tracks designated as main melody tracks by annotator
					if (track.is_main_melody === true) {
						mainMelodyTracks.push(track);
					}
				}
			}
			// Check if annotation blocks are completed (submitted)
			// Traverse through both meta-data and instrumental tracks
			// But skip instrumental tracks that are marked for deletion
			if (track.type === "instrument" && track.marked_for_deletion === true) {
				continue;
			}

			for (const annotation of trackAnnotations) {
				if (!annotation.formData.completed) {
					incompleteBlocks.push(annotation);
				}
			}
		}
		
		// Step 1: check validations and build error array:
		if (!songLevelComplete) {
			errors.push(`Song-Level Annotation Form was not submitted.
歌曲层级标注仍未被提交，请检查后提交。`);
		}
		if (!durationComplete) {
			errors.push(`Please fill in a value for Duration - Number of Bars.
请填写歌曲时长 - 小节数。`);
		}
		if (!timeSignatureComplete) {
			errors.push(`Time Signature List is empty. Please fill in at least one time signature change event.
拍号列表是空的。请至少填写一个拍号。`);
		}
		if (!keyComplete) {
			errors.push(`Key List is empty. Please fill in at least one key change event.
曲调列表是空的。请至少填写一个曲调。`);
		}
		if (!tempoComplete) {
			errors.push(`Tempo List is empty. Please fill in at least one tempo change event.
曲速列表是空的。请至少填写一个曲速。`);
		}
		if (!musicalScoreApplied) {
			errors.push(`There are unsaved changes to the Musical Score Info section. Please confirm and press the "Apply Changes (确认修改)" button.
谱例信息部分有未保存的修改。请确认并点击"Apply Changes (确认修改)"键。`);
		}
		if (!musicStructureTrackHasBlock) {
			errors.push(`You have added 0 annotation blocks to the Music Structure Track. Please go back and complete the bar-level annotations.
您未在乐器结构轨上添加任何标注方块。请返回并完成小节层级标注。`);
		}
		if (!chordTrackHasBlock) {
			errors.push(`You have added 0 annotation blocks to the Chord Structure Track. Please go back and complete the bar-level annotations.
您未在和弦轨上添加任何标注方块。请返回并完成小节层级标注。`);
		}
		if (!hasValidInstrumentTrack) {
			errors.push(`There are 0 valid instrumental tracks (i.e., instrumental tracks not marked for deletion). Please go back and complete the bar-level annotations. If this file truly contains no valid instrumental tracks, do not submit it; instead, report it to the admin team so they can remove it from our database.
当前文件中没有有效的乐器轨道(即乐器轨道数为0或所有乐轨均被标记为应删除乐轨)。请返回并完成小节层级的标注。如果该文件确实没有有效的乐器轨道，请不要提交，而是立刻通知管理员团队，以便他们从数据库中删除该文件。
`);
		}
		if (hasUnknownTrack) {
			errors.push(`Some Unknown Tracks have not been assigned an instrument. Please ensure that every track not marked for deletion has an instrument assigned.
部分乐轨尚未选择合适的乐器（仍为未知乐轨）。请确保所有未标记为“应删除乐轨”的轨道都已选择正确的乐器。`);
        }
		if (instrumentTracksMissingBlock.length != 0) {
			let trackList = instrumentTracksMissingBlock.map((track, index) => {
				const { name, id } = track;
				return `${index + 1}. Name: ${name} — ID: ${id}`;
			}).join('\n');
			
			errors.push(`Some instrumental tracks have neither been annotated (i.e., there is 0 annotation block added) nor marked for deletion. Please return and complete the bar-level annotations.
部分乐器轨道既未进行标注（即未添加任何标注方块），也未被标记为应删除乐轨。请返回并完成小节层级的标注。

${trackList}`);
		}
		if (incompleteBlocks.length != 0) {
			let blockList = incompleteBlocks.map((block, index) => {
				const { id, trackId, startMeasure, startBeat, startSubdivision, endMeasure, endBeat, endSubdivision } = block;
				return `${index + 1}. ID: ${id} — trackID: ${trackId} — Position: ${startMeasure}.${startBeat}.${startSubdivision} - ${endMeasure}.${endBeat}.${endSubdivision}`;
			}).join('\n');
			
			errors.push(`Certain annotation blocks have not been submitted yet. Please submit these annotation blocks before submitting the annotation sheet.
您仍未提交某些标注方块。

${blockList}`);
		}

		// If file contains main melody, check for presence of main melody check
		console.log("Num melody tracks: ")
		console.log(mainMelodyTracks.length)
		console.log("presence_of_main_melody: ")
		console.log(generalInfo?.presence_of_main_melody)
		if (generalInfo?.presence_of_main_melody?.[0] === "Main Melody + Accompaniment (主旋律 + 伴奏)") {
			console.log('enter1')
			if (mainMelodyTracks.length === 0) {
				console.log('enter2')
				errors.push(`You indicated the presence of a main melody in the Song-Level Annotation, but no valid track has been marked as the main melody track(s)! Please designate at least one valid track (i.e., not marked for deletion) as the main melody track.
您在歌曲层级标注中指明了该文件存在主旋律，但尚未将任何有效乐轨标记为主旋律乐轨！请至少将一条有效乐轨（即未被标记为应删除乐轨的轨道删）指定为主旋律乐轨。`);
			}
		}

		else if (generalInfo?.presence_of_main_melody?.[0] === "Accompaniment Only (纯伴奏)") {
			if (mainMelodyTracks.length != 0) {
				let trackList = mainMelodyTracks.map((track, index) => {
					const { name, id } = track;
					return `${index + 1}. Name: ${name} — ID: ${id}`;
				}).join('\n');
				errors.push(`You indicated that a main melody is not present in the Song-Level Annotation, but there exist tracks that have been marked as the main melody track(s)! Please correct the errors before submission.
您在歌曲层级标注中指明了该文件不存在主旋律，但有些乐轨仍被标记成了主旋律轨。请修正这些错误！
				
${trackList}`);
			}
		}

		if (errors.length > 0) {
			setValidationErrors(errors);
  			setAlertOpen(true);
			return;
		}

		// Step 2: Execute save and submit logic
		try {
			setLoading(true);
			
			// First save current changes
			const saveSuccess = await saveAnnotationSheet(
				userData.user_id,
				sheetId,
				sessionId,
				"Completed"
			);
			if (!saveSuccess) {
				alert('Save failed, please try again.');
				return;
			}
			
			// Step 3: Show success popup
			setSubmitModalOpen(true);
			
			// Step 4: Navigate back to parent folder after 3 seconds
			setTimeout(() => {
				setSubmitModalOpen(false);
				setTimeout(() => {
					const backPath = originalPathArray.join("/");
					if (backPath) {
						navigate(`/?path=${encodeURIComponent(backPath)}`);
					} else {
						navigate('/');
					}
				}, 200); // Small delay to ensure modal closes smoothly
			}, 3000);
			
		} catch (error) {
			console.error('Submission failed:', error);
			alert('An error occurred during submission, please try again.');
		} finally {
			setLoading(false);
		}
	}

	const onDialogSave = async () => {
		if (!editorActionsEnabled) return;
		batchEditorRef.current.handleApply();
		setUnAppliedChanges(false);
		await handleSave(false);
		setShowUnsavedDialog(false);
	};

	const onDialogDontSave = async () => {
		if (!editorActionsEnabled) return;
		setShowUnsavedDialog(false);
		batchEditorRef.current.discardChanges();
		setUnAppliedChanges(false);
		const success = await saveAnnotationSheet(userData.user_id, sheetId, sessionId);
		if (success) {
			alert("Annotation sheet saved successfully!");
		} else {
			alert("Failed to save annotation sheet. Please try again.");
		}
		annotationDataService.notifyListeners(); // this refreshes ui
	};

	const onDialogCancel = () => {
		setShowUnsavedDialog(false);
	};

	// save sheet handler
	const handleSave = async (autosave) => {
		try {
			if (mode !== "edit" || !accessHealthy) return false;
			if (!autosave) {
				batchEditorRef.current.handleApply();
			}
			const success = await saveAnnotationSheet(userData.user_id, sheetId, sessionId);
			const message = getSaveAlertMessage({
				autosave,
				outcome: success ? "success" : "failure",
			});
			if (message) alert(message);
			return success;
		} catch (error) {
			console.error("Unexpected annotation sheet save error:", error);
			const message = getSaveAlertMessage({ autosave, outcome: "error" });
			if (message) alert(message);
			return false;
		}
	};
  
  // Drag and drop midi file
  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (!editorActionsEnabled) return;

      const files = event.dataTransfer.files;
      if (files.length > 1) {
        alert("Please drop only one MIDI file.");
        return;
      }
      const file = files[0];
      if (!file) return;

      if (file.name.endsWith(".mid") || file.name.endsWith(".midi")) {
        uploadMidi({ file, sheetId, sessionId, onLoadingChange: setUploadLoading });
      } else {
        alert("Please drop a valid .mid or .midi file");
      }
    },
    [editorActionsEnabled, sessionId, sheetId]
  );

  const handleDragOver = (event) => {
    event.preventDefault();
  };

	const getSnappedPlayheadX = (clientX) => {
		const gridBounds = sharedScrollContainerRef.current.getBoundingClientRect();
		clientX = clientX <= gridBounds.left ? gridBounds.left : clientX;
		clientX = clientX >= gridBounds.right ? gridBounds.right : clientX;
		const relativeX = clientX - gridBounds.left + sharedScrollContainerRef.current.scrollLeft;

		const SUBDIVISIONS_PER_BEAT = 4;
		const showSubdivisions = horizontalZoomLevel >= 1.5; // same as TimeGrid
		const snapSize = (40 * dynamicZoomValue) / (showSubdivisions ? SUBDIVISIONS_PER_BEAT : 1);

		return Math.round(relativeX / snapSize) * snapSize;
	};

	const handlePlayheadMouseDown = (e) => {
		setIsPlayheadDragging(true);
		const snappedX = getSnappedPlayheadX(e.clientX);
		setPlayheadX(snappedX);
	};

	const handlePlayheadMouseMove = (e) => {
		if (!isPlayheadDragging) return;

		const snappedX = getSnappedPlayheadX(e.clientX);
		setPlayheadX(Math.max(0, snappedX));

		const scrollContainer = sharedScrollContainerRef.current;
		if (!scrollContainer) return;

		const bounds = scrollContainer.getBoundingClientRect();
		const edgeThreshold = 50;
		const cursorX = e.clientX - bounds.left;

		if (cursorX < edgeThreshold) {
			scrollDirectionRef.current = 'left';
		} else if (cursorX > scrollContainer.clientWidth - edgeThreshold) {
			scrollDirectionRef.current = 'right';
		} else {
			scrollDirectionRef.current = null;
		}
	};

	// Auto scrolling even if cursor paused
	useEffect(() => {
		if (isPlayheadDragging) {
			scrollSpeedRef.current = 1; // reset on start

			scrollIntervalRef.current = setInterval(() => {
				const scrollContainer = sharedScrollContainerRef.current;
				const dir = scrollDirectionRef.current;
				if (!scrollContainer || !dir) return;

				// increase speed
				scrollSpeedRef.current = Math.min(scrollSpeedRef.current + 0.2, 10); // cap speed at 10
				const baseAmount = 20;
				const speed = baseAmount * scrollSpeedRef.current;

				const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
				const maxPlayheadX = scrollContainer.scrollWidth;

				if (dir === 'left') {
					scrollContainer.scrollLeft = Math.max(scrollContainer.scrollLeft - speed, 0);
					setPlayheadX(prev => Math.max(prev - speed, 0));
				} else if (dir === 'right') {
					scrollContainer.scrollLeft = Math.min(scrollContainer.scrollLeft + speed, maxScrollLeft);
					setPlayheadX(prev => Math.min(prev + speed, maxPlayheadX));
				}

			}, 30);
		} else {
			clearInterval(scrollIntervalRef.current);
			scrollSpeedRef.current = 1;
		}

		return () => clearInterval(scrollIntervalRef.current);
	}, [isPlayheadDragging]);

	const handlePlayheadMouseUp = () => {
		setIsPlayheadDragging(false);
	};

	// Handle shared scrolling for all tracks
	const handleSharedScroll = useCallback((e) => {
		// Use requestAnimationFrame to throttle scroll updates for better performance
		if (scrollAnimationFrameRef.current) {
			cancelAnimationFrame(scrollAnimationFrameRef.current);
		}
		
		scrollAnimationFrameRef.current = requestAnimationFrame(() => {
			if (sharedScrollContainerRef.current) {
				setSharedScrollLeft(e.target.scrollLeft);
			}
		});
	}, []);

	const handleTimelineWheel = useCallback((event) => {
		const zoomIntent = getTimelineWheelZoomIntent(event);
		if (zoomIntent) {
			const pointerPosition = getTimelinePointerPosition(event.clientX);
			if (!pointerPosition) return;
			event.preventDefault();
			if (zoomIntent.axis === 'horizontal') {
				captureHorizontalZoomAnchor(event.clientX);
				adjustHorizontalZoom(zoomIntent.direction);
			} else {
				adjustVerticalZoom(zoomIntent.direction);
			}
			return;
		}
		const delta = getHorizontalTimelineDelta(event);
		if (!delta || !sharedScrollContainerRef.current) return;
		event.preventDefault();
		sharedScrollContainerRef.current.scrollLeft += delta;
	}, [adjustHorizontalZoom, adjustVerticalZoom, captureHorizontalZoomAnchor, getTimelinePointerPosition]);

	useEffect(() => {
		if (!timelineInteractionElement) return undefined;
		timelineInteractionElement.addEventListener('wheel', handleTimelineWheel, {passive: false, capture: true});
		return () => timelineInteractionElement.removeEventListener('wheel', handleTimelineWheel, true);
	}, [handleTimelineWheel, timelineInteractionElement]);

	useEffect(() => {
		const handleTimelineZoomShortcut = event => {
			if (
				isFormOpen ||
				pasteDialogOpen ||
				isTypingElement(document.activeElement)
			) return;
			const zoomIntent = getTimelineKeyboardZoomIntent(event);
			if (!zoomIntent) return;
			event.preventDefault();
			if (zoomIntent.axis === 'horizontal') {
				if (!captureHorizontalZoomAnchor(null)) return;
				adjustHorizontalZoom(zoomIntent.direction);
			} else {
				adjustVerticalZoom(zoomIntent.direction);
			}
		};
		window.addEventListener('keydown', handleTimelineZoomShortcut);
		return () => window.removeEventListener('keydown', handleTimelineZoomShortcut);
	}, [
		adjustHorizontalZoom,
		adjustVerticalZoom,
		captureHorizontalZoomAnchor,
		isFormOpen,
		pasteDialogOpen,
	]);

	useEffect(() => {
		if (!timelineInteractionElement) return undefined;

		const handleGestureStart = event => {
			if (!getTimelinePointerPosition(event.clientX)) return;
			event.preventDefault();
			const axis = event.altKey ? 'vertical' : 'horizontal';
			gestureZoomRef.current = {
				axis,
				startLevel: axis === 'vertical' ? verticalZoomLevel : horizontalZoomLevel,
			};
			if (axis === 'horizontal') captureHorizontalZoomAnchor(event.clientX);
		};

		const handleGestureChange = event => {
			const gesture = gestureZoomRef.current;
			if (!gesture) return;
			event.preventDefault();
			if (gesture.axis === 'vertical') {
				setVerticalZoomLevelValue(getTimelineGestureZoomLevel({
					startLevel: gesture.startLevel,
					scale: event.scale,
					sensitivity: 0.3,
					min: ZOOM_RANGE.VERTICAL.MIN,
					max: ZOOM_RANGE.VERTICAL.MAX,
				}));
			} else {
				captureHorizontalZoomAnchor(event.clientX);
				setHorizontalZoomLevelValue(getTimelineGestureZoomLevel({
					startLevel: gesture.startLevel,
					scale: event.scale,
					sensitivity: 0.8,
					min: ZOOM_RANGE.HORIZONTAL.MIN,
					max: ZOOM_RANGE.HORIZONTAL.MAX,
				}));
			}
		};

		const handleGestureEnd = event => {
			if (gestureZoomRef.current) event.preventDefault();
			gestureZoomRef.current = null;
		};

		timelineInteractionElement.addEventListener('gesturestart', handleGestureStart, {passive: false, capture: true});
		timelineInteractionElement.addEventListener('gesturechange', handleGestureChange, {passive: false, capture: true});
		timelineInteractionElement.addEventListener('gestureend', handleGestureEnd, {passive: false, capture: true});
		return () => {
			timelineInteractionElement.removeEventListener('gesturestart', handleGestureStart, true);
			timelineInteractionElement.removeEventListener('gesturechange', handleGestureChange, true);
			timelineInteractionElement.removeEventListener('gestureend', handleGestureEnd, true);
		};
	}, [
		captureHorizontalZoomAnchor,
		getTimelinePointerPosition,
		horizontalZoomLevel,
		setHorizontalZoomLevelValue,
		setVerticalZoomLevelValue,
		timelineInteractionElement,
		verticalZoomLevel,
	]);

	// Add ref for scroll animation frame
	const scrollAnimationFrameRef = useRef();

	useEffect(() => {
		if (isPlayheadDragging) {
			document.body.style.userSelect = "none";
		} else {
			document.body.style.userSelect = "";
		}
		if (isPlayheadDragging) {
			window.addEventListener("mousemove", handlePlayheadMouseMove);
			window.addEventListener("mouseup", handlePlayheadMouseUp);
		} else {
			window.removeEventListener("mousemove", handlePlayheadMouseMove);
			window.removeEventListener("mouseup", handlePlayheadMouseUp);
		}
		return () => {
			window.removeEventListener("mousemove", handlePlayheadMouseMove);
			window.removeEventListener("mouseup", handlePlayheadMouseUp);
			document.body.style.userSelect = "";
			// Clean up scroll animation frame on unmount
			if (scrollAnimationFrameRef.current) {
				cancelAnimationFrame(scrollAnimationFrameRef.current);
			}
		};
	}, [isPlayheadDragging]);

	// Handle window resize to maintain playhead musical position
	useEffect(() => {
		let resizeTimeout;
		let storedMusicalPosition = null;
		
		const handleWindowResize = () => {
			// Clear any existing timeout to debounce resize events
			if (resizeTimeout) {
				clearTimeout(resizeTimeout);
			}
			
			// Store the current musical position before resize
			if (!storedMusicalPosition && sharedScrollContainerRef.current && beatsPerMeasureMap.length && timeSignatures.length && playheadX > 0) {
				const actualPixelsPerBeat = 40 * dynamicZoomValue;
				storedMusicalPosition = toMusicalPositionWithGridLevel(
					playheadX, 
					'bar', // Use bar level for consistent snapping to bar starts
					false, // Don't use proximity for exact position
					beatsPerMeasureMap, 
					actualPixelsPerBeat, 
					timeSignatures
				);
			}
			
			// Debounce resize handling to avoid excessive calculations
			resizeTimeout = setTimeout(() => {
				if (!sharedScrollContainerRef.current || !beatsPerMeasureMap.length || !timeSignatures.length || !storedMusicalPosition) {
					storedMusicalPosition = null;
					return;
				}

				// Recalculate pixel position based on stored musical position
				requestAnimationFrame(() => {
					const actualPixelsPerBeat = 40 * dynamicZoomValue;
					const newPixelPosition = calculatePosition(
						storedMusicalPosition.measure,
						storedMusicalPosition.beat,
						storedMusicalPosition.subdivision,
						beatsPerMeasureMap,
						actualPixelsPerBeat,
						timeSignatures
					);

					// Update playhead position - now it's relative to the grid area, not the screen
					setPlayheadX(newPixelPosition);
					
					// Clear stored position after update
					storedMusicalPosition = null;
				});
			}, 150); // 150ms debounce delay for better stability
		};

		// Add resize event listener
		window.addEventListener('resize', handleWindowResize);

		// Cleanup
		return () => {
			window.removeEventListener('resize', handleWindowResize);
			if (resizeTimeout) {
				clearTimeout(resizeTimeout);
			}
		};
	}, [playheadX, dynamicZoomValue, beatsPerMeasureMap, timeSignatures]);

	const handleBackToFolder = (e) => {
		// Navigate back to DriveFolder (root "//" or wherever you mount DriveFolder),
		// appending the same ?path= that we received.
		const backPath = originalPathArray.join("/");
		if (backPath) {
			navigate(`/?path=${encodeURIComponent(backPath)}`);
		} else {
			navigate(`/`);
		}
	}

	// Handle grid level changes from TimeGrid
	const handleGridLevelChange = useCallback((gridLevel) => {
		setSharedGridLevel(gridLevel);
	}, []);

	// Context Menu Handlers
	// const contextHandlers = [
	// 	{ 
	// 		label: 'Copy', 
	// 		onClick: handleCopy,
	// 		disabled: !selectedAnnotationId,
	// 	},
	// 	{ 
	// 		label: 'Paste',          
	// 		onClick: handlePaste,
	// 		disabled: !copiedWidth,
	// 	}
	// ];

	const handleAccessNoticeAcknowledge = () => {
		const dismissLocation = getAccessNoticeDismissLocation({
			notice: accessNotice,
			search: location.search,
		});
		clearAccessNotice();
		if (dismissLocation) navigate(dismissLocation, {replace: true});
	};

	const handleSectionToggle = (section) => {
		setExpandedSection((currentSection) => currentSection === section ? null : section);
	};

	const accessDialog = (
		<AnnotationSheetAccessDialog
			notice={accessNotice}
			onAcknowledge={handleAccessNoticeAcknowledge}
		/>
	);

	// Show loading state
	if (!accessReady || !isInitialized) {
		return (
			<>
				<LoadingSpinner message="Loading annotation sheet..."/>
				{accessDialog}
			</>
		);
	}

	// Show instrument validation error state
	if (instrumentValidationError) {
		return (
			<InstrumentValidationErrorDialog
				open={true}
				onReturn={handleBackToFolder}
				invalidTracks={instrumentValidationError.invalidTracks}
			/>
		);
	}

	// Show error state
	if (error) {
		return (
			<div className="flex items-center justify-center h-screen">
				<div className="text-center text-red-500">
					<p>{error}</p>
					<button
						className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
						onClick={() => window.location.reload()}
					>
						Retry
					</button>
				</div>
			</div>
		);
	}

	return (
		<><div ref={sharedScrollRef} className="min-h-screen flex flex-col bg-white"
				 style={{height: "auto", minHeight: "100vh", overflowY: "auto"}}
				 onDragOver={editorActionsEnabled ? handleDragOver : undefined}
				 onDrop={editorActionsEnabled ? handleDrop : undefined}>
			<div className="w-full p-2 lg:p-4">
				<div className="bg-white mb-4">
					<ErrorValidationDialog
						open={alertOpen}
						onClose={() => setAlertOpen(false)}
						errors={validationErrors}
					/>
					{/* Header with Logo, Song Name, Save, MIDI buttons and Logout */}
					<Box display="flex" justifyContent="space-between" alignItems="center" mb={3} sx={{ borderBottom: '1px solid #eaeaea', pb: 2 }}>
						{/* Left side: Logo and Song Name */}
						<Box display="flex" alignItems="center" gap={2}>
              <ArrowBackIcon onClick={handleBackToFolder} className="cursor-pointer"/>
							<img 
								src={NeuroMuseLogo}
								alt="NeuroMuse Logo" 
								style={{ 
									height: '40px', 
									width: 'auto', 
									objectFit: 'contain', 
									cursor: 'pointer' 
								}}
								onClick={() => navigate("/")}
							/>
							
							<Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: '#333' }}>
								{generalInfo?.displayed_filename || 'Untitled Song'}
							</Typography>
						</Box>
						
						{/* Right side: All buttons and UserProfile */}
						<Box display="flex" gap={1.5} alignItems="center">
							{/*<FilePickerButton sheetId={sheetId} loading={uploadLoading} setLoading={setUploadLoading}/>*/}

							<Tooltip title="Download MIDI (下载MIDI)" arrow>
								<span onClick={handleDownloadMidi} style={{cursor: 'pointer'}}>
									<SimCardDownloadOutlined/>
								</span>
							</Tooltip>

							{/* Combined View/Edit Mode Dropdown */}
							{(isAdmin && isAdminMode) && <Button
								onClick={handleMenuOpen}
								disabled={isTransitioning || !accessHealthy}
								endIcon={<ExpandMoreIcon fontSize="small"/>}
								sx={{
									width: "232px",
									px: 0,
									py: 0, // Match the py-1.5 (6px) of other buttons
									backgroundColor: "#f4f5f7ff",
									color: "black",
									border: "0px solid black",
									borderRadius: "6px",
									fontWeight: 500,
									textTransform: "none",
									height: "35px", 
									fontSize: "13px", 
									"&:hover": {
										backgroundColor: "grey.50",
									},
								}}
							>
								{isViewMode ? (
									<>
										<Box component={ViewIcon} sx={{width: 20, height: 20, mr: 1, flexShrink: 0}}/>
										Viewing
									</>
								) : isTrackEditMode ? (
									<>
										<Box component={EditIcon} sx={{width: 20, height: 20, mr: 1, flexShrink: 0}}/>
										Editing Tracks
									</>
								) : (
									<>
										<Box component={EditIcon} sx={{width: 20, height: 20, mr: 1, flexShrink: 0}}/>
										Editing
									</>
								)}
							</Button>}
							<Menu
								anchorEl={anchorEl}
								open={Boolean(anchorEl)}
								onClose={handleMenuClose}
								anchorOrigin={{vertical: "bottom", horizontal: "right"}}
								transformOrigin={{vertical: "top", horizontal: "right"}}
								PaperProps={{
									sx: {
										width: "280px",
										borderRadius: "8px",
										boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
									},
								}}
							>
								<MenuItem
									onClick={() => handleModeChange("view")}
									disabled={isTransitioning}
									sx={{
										px: 3,
										py: 2,
										"&:hover": {backgroundColor: "grey.50"},
										color: "black",
										backgroundColor: isViewMode ? "grey.100" : "transparent",
										flexDirection: "column",
										alignItems: "flex-start",
										minHeight: "60px",
									}}
								>
									<Box display="flex" alignItems="center" width="100%">
										<Box component={ViewIcon} sx={{width: 20, height: 20, mr: 2, flexShrink: 0}}/>
										<Box>
											<Typography variant="body1" sx={{fontWeight: 600, fontSize: "14px", lineHeight: 1.2}}>
												Viewing
											</Typography>
											<Typography variant="body2" sx={{color: "grey.600", fontSize: "12px", lineHeight: 1.3, mt: 0.5}}>
												View without making changes
											</Typography>
										</Box>
									</Box>
								</MenuItem>
								<MenuItem
									onClick={() => handleModeChange("edit")}
									disabled={isTransitioning}
									sx={{
										px: 3,
										py: 2,
										"&:hover": {backgroundColor: "grey.50"},
										color: "black",
										backgroundColor: (!isViewMode && !isTrackEditMode) ? "grey.100" : "transparent",
										flexDirection: "column",
										alignItems: "flex-start",
										minHeight: "60px",
									}}
								>
									<Box display="flex" alignItems="center" width="100%">
										<Box component={EditIcon} sx={{width: 20, height: 20, mr: 2, flexShrink: 0}}/>
										<Box>
											<Typography variant="body1" sx={{fontWeight: 600, fontSize: "14px", lineHeight: 1.2}}>
												Editing
											</Typography>
											<Typography variant="body2" sx={{color: "grey.600", fontSize: "12px", lineHeight: 1.3, mt: 0.5}}>
												Edit annotations only
											</Typography>
										</Box>
									</Box>
								</MenuItem>
								<MenuItem
									onClick={() => handleModeChange("track-edit")}
									disabled={isTransitioning}
									sx={{
										px: 3,
										py: 2,
										"&:hover": {backgroundColor: "grey.50"},
										color: "black",
										backgroundColor: (!isViewMode && isTrackEditMode) ? "grey.100" : "transparent",
										flexDirection: "column",
										alignItems: "flex-start",
										minHeight: "60px",
									}}
								>
									<Box display="flex" alignItems="center" width="100%">
										<Box component={EditIcon} sx={{width: 20, height: 20, mr: 2, flexShrink: 0}}/>
										<Box>
											<Typography variant="body1" sx={{fontWeight: 600, fontSize: "14px", lineHeight: 1.2}}>
												Editing Tracks
											</Typography>
											<Typography variant="body2" sx={{color: "grey.600", fontSize: "12px", lineHeight: 1.3, mt: 0.5}}>
												Add and delete tracks
											</Typography>
										</Box>
									</Box>
								</MenuItem>
							</Menu>

							<Dialog
								open={showUnsavedDialog}
								onClose={() => setShowUnsavedDialog(false)}
								aria-labelledby="unsaved-dialog-title"
								aria-describedby="unsaved-dialog-description"
								maxWidth="xs"
							>
								<DialogTitle id="unsaved-dialog-title">
									You made changes to the musical score info but did not apply the changes. Do you want to save these
									changes?
									<br/> <br/>
									您修改了谱例信息，但并没有点击确认修改按钮。您现在是否想保存这些修改？
								</DialogTitle>
								<DialogContent dividers>
									<DialogContentText id="unsaved-dialog-description" component="div">
										{/* "Original vs New" for TimeSignature */}
										<div>
											{/* Original: TimeSignatures; New: TimeSignatures */}
											{batchEditorRef.current?.computeTimeSignatureDifference() && (
												<>
													<strong>Time Signature List Change:</strong>
													<br/>
													<pre style={{fontFamily: 'monospace', whiteSpace: 'pre-wrap'}}>
														Original: [
														{batchEditorRef.current?.computeTimeSignatureDifference().original.map((ts, idx) => (
															<span
																key={idx}>{`${ts.measure} → ${ts.timeSignature}${idx < batchEditorRef.current?.computeTimeSignatureDifference().original.length - 1 ? ', ' : ''}`}</span>
														))}
														]
														<br/>
														New: [
														{batchEditorRef.current?.computeTimeSignatureDifference().current.map((ts, idx) => (
															<span
																key={idx}>{`${ts.measure} → ${ts.timeSignature}${idx < batchEditorRef.current?.computeTimeSignatureDifference().current.length - 1 ? ', ' : ''}`}</span>
														))}
														]
													</pre>
												</>
											)}

											{/* Original: Key; New: Key */}
											{batchEditorRef.current?.computeKeyDifference() && (
												<>
													<strong>Key List Change:</strong>
													<br/>
													<pre style={{fontFamily: 'monospace', whiteSpace: 'pre-wrap'}}>
														Original: [
														{batchEditorRef.current?.computeKeyDifference().original.map((ts, idx) => (
															<span
																key={idx}>{`${ts.position.bar}:${ts.position.beat}:${ts.position.subdivision} → ${ts.value.note} ${ts.value.mode}${idx < batchEditorRef.current?.computeKeyDifference().original.length - 1 ? ', ' : ''}`}</span>
														))}
														]
														<br/>
														New: [
														{batchEditorRef.current?.computeKeyDifference().current.map((ts, idx) => (
															<span
																key={idx}>{`${ts.position.bar}:${ts.position.beat}:${ts.position.subdivision} → ${ts.value.note} ${ts.value.mode}${idx < batchEditorRef.current?.computeKeyDifference().current.length - 1 ? ', ' : ''}`}</span>
														))}
														]
													</pre>
												</>
											)}

											{/* Original: Tempo; New: Tempo */}
											{batchEditorRef.current?.computeTempoDifference() && (
												<>
													<strong>Tempo List Change:</strong>
													<br/>
													<pre style={{fontFamily: 'monospace', whiteSpace: 'pre-wrap'}}>
														Original: [
														{batchEditorRef.current?.computeTempoDifference().original.map((ts, idx) => (
															<span
																key={idx}>{`${ts.position.bar}:${ts.position.beat}:${ts.position.subdivision} → ${ts.value} ${idx < batchEditorRef.current?.computeTempoDifference().original.length - 1 ? ', ' : ''}`}</span>
														))}
														]
														<br/>
														New: [
														{batchEditorRef.current?.computeTempoDifference().current.map((ts, idx) => (
															<span
																key={idx}>{`${ts.position.bar}:${ts.position.beat}:${ts.position.subdivision} → ${ts.value} ${idx < batchEditorRef.current?.computeTempoDifference().current.length - 1 ? ', ' : ''}`}</span>
														))}
														]
													</pre>
												</>
											)}

											{/* Original: Duration; New: Duration */}
											{batchEditorRef.current?.computeDurationDifference() && (
												<>
													<strong>Duration (number of bars) Change:</strong>
													<br/>
													<pre style={{fontFamily: 'monospace', whiteSpace: 'pre-wrap'}}>
														Original: <span> {batchEditorRef.current?.computeDurationDifference().original}</span>
														<br/>
														New: <span>{batchEditorRef.current?.computeDurationDifference().current}</span>
													</pre>
												</>
											)}
										</div>
									</DialogContentText>
								</DialogContent>
								<DialogActions style={{flexDirection: 'column', gap: '8px', padding: '16px'}}>
									<Button onClick={onDialogSave} disabled={!editorActionsEnabled} color="primary" variant="contained" fullWidth style={{
										backgroundColor: '#000',
										color: '#fff',
										borderRadius: '12px',
										fontWeight: 'bold',
										whiteSpace: 'normal',
										textAlign: 'center',
										lineHeight: '1.2',
										padding: '8px 12px',
										minHeight: '48px',
									}}>
										Save All (保存全部)
									</Button>
									<Button onClick={onDialogDontSave} disabled={!editorActionsEnabled} color="error" variant="outlined" fullWidth style={{
										borderColor: '#f44336',
										color: '#f44336',
										borderRadius: '12px',
										fontWeight: 'bold',
										whiteSpace: 'normal',
										textAlign: 'center',
										lineHeight: '1.2',
										padding: '8px 12px',
										minHeight: '48px',
									}}>
										Discard Musical Score Info Change and Save Others (删除谱例信息变动并保存其他变动)
									</Button>
									<Button onClick={onDialogCancel} variant="outlined" fullWidth style={{
										borderColor: '#999',
										color: '#555',
										borderRadius: '12px',
										fontWeight: 'bold',
										whiteSpace: 'normal',
										textAlign: 'center',
										lineHeight: '1.2',
										padding: '8px 12px',
										minHeight: '48px',
									}}>
										Cancel
									</Button>
								</DialogActions>
							</Dialog>

							<button
								className="px-4 py-1.5 border border-blue-500 text-blue-500 rounded-md hover:bg-blue-50 transition-colors duration-200 font-medium text-sm whitespace-nowrap"
								onClick={handleClickSave}
								disabled={loading || !editorActionsEnabled}
							>
								Save File
							</button>

							<button
								className="px-4 py-1.5 bg-blue-400 text-white rounded-md hover:bg-blue-600 transition-colors duration-200 font-medium text-sm"
								onClick={handleSubmit}
								disabled={loading || !editorActionsEnabled}
							>
								Submit
							</button>

							{/* Thin gray divider */}
							<div
								style={{
									width: '1px',
									height: '32px',
									backgroundColor: '#d1d5db',
									margin: '0 8px'
								}}
							/>

							<UserProfile userName={userName} handleLogout={handleLogout}/>
						</Box>
					</Box>

				</div>
				{/* Song Level Annotation */}
				<div className="bg-white mb-4">
					<h2 className="m-0">
						<button
							type="button"
							id="song-level-annotation-header"
							className="w-full px-4 py-3 flex items-center justify-between text-left cursor-pointer bg-transparent border-0"
							onClick={() => handleSectionToggle("song")}
							aria-expanded={expandedSection === "song"}
							aria-controls="song-level-annotation-content"
						>
							<span className="flex items-center space-x-2">
								<span className="text-xl font-semibold">
									Song Level Annotation (歌曲层级注释)
								</span>
								{generalInfo?.completed ? (
									<span
										className="bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded-full ml-2"
										title="Completed"
									>
										Completed
									</span>
								) : (
									<span
										className="bg-gray-400 text-white text-xs font-semibold px-3 py-1 rounded-full ml-2"
										title="Not completed"
									>
										Not Completed
									</span>
								)}
							</span>
							<ExpandMoreIcon
								aria-hidden="true"
								style={{
									transform: expandedSection === "song" ? "rotate(180deg)" : "rotate(0deg)",
									transition: "transform 200ms ease-in-out",
								}}
							/>
						</button>
					</h2>

					<Collapse
						id="song-level-annotation-content"
						role="region"
						aria-labelledby="song-level-annotation-header"
						aria-hidden={expandedSection !== "song"}
						inert={expandedSection !== "song"}
						in={expandedSection === "song"}
						timeout={250}
						collapsedSize={0}
					>
						<div className="px-4 py-3 rounded-lg" style={{backgroundColor: COLORS.BG_LIGHT}}>
							{/* Song info and event sections */}
							<div className="space-y-4">

								{/* Song Level Annotation Form */}
								<AnnotationForm
									attributes={SongLevelAttributes}
									initialValues={generalInfo || {}}
									onChange={handleSongGeneralInfoChange}
									onSubmit={(values) => {
										handleSongGeneralInfoUpdate(values);
									}}
								/>
							</div>
						</div>
					</Collapse>
				</div>

				{/* Divider */}
				<div className="border-t-[2px]" style={{borderColor: COLORS.DIVIDER}}></div>

				{/* Bar Settings */}
				<div className="bg-white mb-4 mt-4">
					{/* Panel Header */}
					<h2 className="m-0">
						<button
							type="button"
							id="musical-score-info-header"
							className="w-full px-4 py-3 flex items-center justify-between text-left cursor-pointer bg-transparent border-0"
							onClick={() => handleSectionToggle("score")}
							aria-expanded={expandedSection === "score"}
							aria-controls="musical-score-info-content"
						>
							<span className="text-xl font-semibold">Musical Score Info (谱例信息)</span>
							<ExpandMoreIcon
								aria-hidden="true"
								style={{
									transform: expandedSection === "score" ? "rotate(180deg)" : "rotate(0deg)",
									transition: "transform 200ms ease-in-out",
								}}
							/>
						</button>
					</h2>

					{/* Collapsible Content */}
					<Collapse
						id="musical-score-info-content"
						role="region"
						aria-labelledby="musical-score-info-header"
						aria-hidden={expandedSection !== "score"}
						inert={expandedSection !== "score"}
						in={expandedSection === "score"}
						timeout={250}
						collapsedSize={0}
					>
						<div className="p-4">
							<BarSettingsBatchEditor
								ref={batchEditorRef}
								onApplyChanges={handleBatchTimeSignatureChanges}
								measures={generalInfo.duration_number_of_bars}
								durationAttributes={SongLevelAttributes}
								durationInitialValue={generalInfo || {}}
								onDurationSubmit={handleSongGeneralInfoUpdate}
								onDirtyChange={(dirty) => setUnAppliedChanges(dirty)}
							/>
						</div>
					</Collapse>
				</div>

				{/* Divider */}
				<div className="border-t-[2px]" style={{borderColor: COLORS.DIVIDER}}></div>

				{/* Bar Level Annotation */}
				<div className="bg-white mb-4 mt-4">
					{/* Panel Header */}
					<div className="flex items-center">
						<h2 className="m-0 flex-1">
							<button
								type="button"
								id="bar-level-annotation-header"
								className="w-full px-4 py-3 flex items-center justify-between text-left cursor-pointer bg-transparent border-0"
								onClick={() => handleSectionToggle("bar")}
								aria-expanded={expandedSection === "bar"}
								aria-controls="bar-level-annotation-content"
							>
								<span className="text-xl font-semibold">Bar Level Annotation (小节层级注释)</span>
								<ExpandMoreIcon
									aria-hidden="true"
									style={{
										transform: expandedSection === "bar" ? "rotate(180deg)" : "rotate(0deg)",
										transition: "transform 200ms ease-in-out",
									}}
								/>
							</button>
						</h2>
						<div className="pr-4">
							<InfoTooltip
								instruction={BarLevelAnnotationInstruction}
								iconProps={{"aria-label": "Bar level help"}}
							/>
						</div>
					</div>

					{/* Collapsible Content */}
					<Collapse
						id="bar-level-annotation-content"
						role="region"
						aria-labelledby="bar-level-annotation-header"
						aria-hidden={expandedSection !== "bar"}
						inert={expandedSection !== "bar"}
						in={expandedSection === "bar"}
						timeout={250}
						collapsedSize={0}
					>
						{/* Control Panel Section */}
						<div className="p-4">
							{/* Side-by-side Control Panel */}
							<div className="mb-6 flex flex-row gap-12">
								{/* Zoom Settings Section */}
								<div className="flex-1">
									<h3 className="text-xs font-medium mb-3 text-gray-500">
										Zoom Settings (缩放设置)
									</h3>
									<div className="flex flex-row gap-8 max-w-4xl">
										<div className="flex items-center gap-3">
											<label htmlFor="horizontal-zoom-new" className="w-24 text-gray-600 whitespace-nowrap">Horizontal (横向):</label>
											<div className="flex-1 flex items-center gap-3">
												<input type="range" id="horizontal-zoom-new" min={ZOOM_RANGE.HORIZONTAL.MIN} max={ZOOM_RANGE.HORIZONTAL.MAX} step={ZOOM_RANGE.HORIZONTAL.STEP} value={horizontalZoomLevel} onChange={handleHorizontalZoomChange} className="flex-1" style={{WebkitAppearance: 'none', appearance: 'none', width: '100%', height: '4px', background: 'black', borderRadius: '2px', outline: 'none', accentColor: 'black', colorScheme: 'dark', marginLeft: '30px'}} />
											</div>
										</div>
										<div className="flex items-center gap-3">
											<label htmlFor="vertical-zoom-new" className="w-24 text-gray-600 whitespace-nowrap">Vertical (纵向):</label>
											<div className="flex-1 flex items-center gap-3">
												<input type="range" id="vertical-zoom-new" min={ZOOM_RANGE.VERTICAL.MIN}
															 max={ZOOM_RANGE.VERTICAL.MAX} step={ZOOM_RANGE.VERTICAL.STEP} value={verticalZoomLevel}
															 onChange={handleVerticalZoomChange} className="flex-1" style={{
													WebkitAppearance: 'none',
													appearance: 'none',
													width: '100%',
													height: '4px',
													background: 'black',
													borderRadius: '2px',
													outline: 'none',
													accentColor: 'black',
													colorScheme: 'dark',
													marginLeft: '30px'
												}}/>
											</div>
										</div>
									</div>
								</div>

								{/* Selected Chord Labeling System Section */}
								<div className="flex-1">
									<h3 className="text-xs font-medium mb-3 text-gray-500">
										Selected Chord Labeling System (和弦标注系统)
									</h3>
									<div className="flex items-center">
										<span className="text-gray-800 font-medium">
											{chordLabellingSystem !== "" ? chordLabellingSystem : "Not selected"}
										</span>
									</div>
								</div>
							</div>

							<div className="mb-6" style={{display: 'none'}}>
								<h3 className="text-lg font-medium mb-3 text-gray-700">
									Zoom Settings (缩放设置)
								</h3>
								<div className="flex flex-row gap-8 max-w-4xl">
									<div className="flex items-center gap-3">
										<label htmlFor="horizontal-zoom" className="w-24 text-gray-600 whitespace-nowrap">Horizontal
											(横向):</label>
										<div className="flex-1 flex items-center gap-3">
											<input
												type="range"
												id="horizontal-zoom"
												min={ZOOM_RANGE.HORIZONTAL.MIN}
												max={ZOOM_RANGE.HORIZONTAL.MAX}
												step={ZOOM_RANGE.HORIZONTAL.STEP}
												value={horizontalZoomLevel}
												onChange={handleHorizontalZoomChange}
												className="flex-1"
												style={{
													WebkitAppearance: 'none',
													appearance: 'none',
													width: '100%',
													height: '4px',
													background: 'black',
													borderRadius: '2px',
													outline: 'none',
													accentColor: 'black',
													colorScheme: 'dark',
													marginLeft: '30px'
												}}
											/>
										</div>
									</div>
									<div className="flex items-center gap-3">
										<label htmlFor="vertical-zoom" className="w-24 text-gray-600 whitespace-nowrap">Vertical
											(纵向):</label>
										<div className="flex-1 flex items-center gap-3">
											<input
												type="range"
												id="vertical-zoom"
												min={ZOOM_RANGE.VERTICAL.MIN}
												max={ZOOM_RANGE.VERTICAL.MAX}
												step={ZOOM_RANGE.VERTICAL.STEP}
												value={verticalZoomLevel}
												onChange={handleVerticalZoomChange}
												className="flex-1"
												style={{
													WebkitAppearance: 'none',
													appearance: 'none',
													width: '100%',
													height: '4px',
													background: 'black',
													borderRadius: '2px',
													outline: 'none',
													accentColor: 'black',
													colorScheme: 'dark',
													marginLeft: '30px'
												}}
											/>
										</div>
									</div>
								</div>
							</div>

						</div>

						<div className="flex items-center justify-center" style={{display: 'none'}}>
							{chordLabellingSystem !== "" && (
								<p
									className="text-center bg-gray-100 text-gray-800 font-medium px-4 py-2 rounded-lg shadow-md border border-gray-300">
									Selected Chord Labeling System (和弦标注系统): <span
									className="font-semibold text-blue-600">{chordLabellingSystem}</span>
								</p>
							)}
						</div>

						{/* Scrollable container for all annotation layers */}
						{/* <ContextMenuWrapper handlers={contextHandlers}> */}
						<div
							ref={attachTimelineInteractionElement}
							className="flex flex-col w-full" 
							style={{position: 'relative'}}
						>
							{/* our Playhead */}
							<Playhead
								playheadX={playheadX}
								handlePlayheadMouseDown={handlePlayheadMouseDown}
								sharedScrollLeft={sharedScrollLeft}
								sharedScrollContainerRef={sharedScrollContainerRef}
								pasteTargetPulseKey={pasteTargetPulseKey}
							/>

							{/* Sticky header containing Add Track button and Time Grid */}
							<div
								className="sticky top-0 bg-white shadow-lg border-b-2 border-black"
								style={{ position: 'sticky', zIndex: 1101 }}
							>
								{!isViewMode && !isTrackEditMode && (
									<Box sx={{ position: 'absolute', top: 8, right: 12, zIndex: 1102 }}>
										<AnnotationToolToggle
											mode={editorInteraction.toolMode}
											onChange={toolMode => dispatchEditorInteraction({ type: 'SET_TOOL', toolMode })}
											disabled={!editorActionsEnabled}
										/>
									</Box>
								)}
								<div className="flex w-full">
									<div className="w-[14%] min-w-[220px] bg-gray-50 border-r border-gray-300">
										<InstrumentMenu
											addNewTrack={addNewTrack}
										/>
									</div>

									{/* Wrap TimeGrid in shared scroll container */}
									<div
										ref={attachSharedScrollContainer}
										data-testid="timeline-scroll-container"
										className="flex-1 overflow-auto bg-white overscroll-x-none touch-pan-y"
										onScroll={handleSharedScroll}
									>
										<TimeGrid
											timeSignatures={timeSignatures}
											keyChanges={keyChanges}
											tempoChanges={tempoChanges}
											beatsPerMeasureMap={beatsPerMeasureMap}
											measures={generalInfo.duration_number_of_bars}
											zoomLevel={dynamicZoomValue}
											onTimeSignatureClick={!isViewMode ? handleTimeSignatureClick : () => {}}
											scrollContainerRef={sharedScrollContainerRef}
											playheadX={playheadX}
											setPlayheadX={setPlayheadX}
											setIsPlayheadDragging={setIsPlayheadDragging}
											onGridLevelChange={handleGridLevelChange}
										/>
									</div>
								</div>
							</div>

							{/* Scrollable Track List */}
							<DndContext
								sensors={sensors}
								collisionDetection={closestCenter}
								onDragEnd={handleDragEnd}
							>
								<SortableContext
									items={tracks.map(t => t.id)}
									strategy={verticalListSortingStrategy}
								>
									<div
										data-testid="annotation-track-lanes"
										tabIndex={0}
										className="w-full overflow-y-auto overscroll-x-none touch-pan-y"
										style={{maxHeight: 'calc(100vh - 220px)'}}
									>
										{tracks.map(track => (
											<SortableTrack key={track.id} id={track.id}>
												<div 
													className={`flex w-full border-b border-black ${track.id === selectedTrackId ? "bg-stone-200" : "bg-white"}`} 
													onClick={() => handleTrackSelect(track.id)} 
													onContextMenuCapture={() => handleTrackSelect(track.id)} 
													key={track.id}
												>
													{track.collapsed ? (
														<div
															className="w-full flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200 cursor-pointer rounded-lg shadow-sm transition-all duration-200"
															onClick={() => toggleTrackCollapse(track.id)}
														>
															<div className="flex items-center space-x-3">
																<div
																	className="text-2xl">{getDynamicIcon((track.name || 'Unknown Track').split("(")[0])}</div>
																<span className="text-lg font-semibold">{track.name || 'Unknown Track'}</span>
															</div>
															<span className="text-sm text-gray-600 italic">Click to expand (点击展开)</span>
														</div>
													) : (
														<>
															<div
																className="w-[14%] min-w-[220px] flex items-stretch"
																onDoubleClick={() => toggleTrackCollapse(track.id)}
															>
																{/* Track label with vertical color bar */}
																<div className="flex w-full">
																	{/* Vertical color bar */}
																	<div
																		className={track.id === selectedTrackId ? "w-4" : "w-2"}
																		style={{
																			backgroundColor: track.marked_for_deletion ? '#9ca3af' : track.colorType,
																			height: `${120 * verticalZoomLevel}px`,
																			marginLeft: '0'
																		}}
																	></div>
																	{/* Track detail */}
																	<div
																		className="cursor-grab flex-1 relative text-black font-bold border-r border-t border-b border-black"
																		style={{height: `${120 * verticalZoomLevel}px`}}
																	>
																		<div className="flex flex-col justify-center relative w-full h-full">
																			{/* tool buttons */}
																			{/* MAIN button - only for instrument tracks and not marked for deletion */}
																			{!isViewMode && track.type === "instrument" && !track.marked_for_deletion && (
																				<div className="absolute top-[9.5px] right-[55px] text-sm" onPointerDown={(e) => e.stopPropagation()}>
																					<Tooltip 
																						title="Click on this icon to highlight and mark the track as a main melody track / 点击此图标以高亮该乐轨，并将其标记为主旋律乐轨" 
																						arrow
																					>
																						<button
																							onClick={(e) => {
																								e.stopPropagation();
																								toggleMainMelodyTrack(track.id);
																							}}
																							className={`px-1 py-0 text-[9px] font-semibold rounded transition-colors duration-200 leading-none h-4 flex items-center justify-center ${
																								track.is_main_melody 
																									? 'bg-blue-500 text-white hover:bg-blue-600' 
																									: 'bg-gray-300 text-gray-600 hover:bg-gray-400'
																							}`}
																							// title="Main Melody Track"
																						>
																							MAIN
																						</button>
																					</Tooltip>
																				</div>
																			)}

															{/* Edit button - only for instrument tracks and not marked for deletion */}
															{!isViewMode && track.id !== "music-structure-track" && track.id !== "chord-track" && !track.marked_for_deletion && (
																<div className="absolute top-[-3.0px] right-8 text-sm" onPointerDown={(e) => e.stopPropagation()}>
																	<MultipleChoice
																		key={`${track.id}-${track.name}-${instrumentChangeDialog.open ? 'dialog-open' : 'dialog-closed'}`}
																		label="Edit Track"
																		options={InstrumentMenuOptions}
																		mandatory={false}
																		allowAnyLevelSelection={false}
																		singleSelect={true}
																		initialValue={[]}
																		onSelect={(val) => {
																			const trackName = val[0];
																			const trackIdPrefix = trackName.concat("-track");
																			const newTrackInstanceIdx = annotationDataService.generateNextTrackIndex(trackName);
																			const newTrackId = trackIdPrefix.concat("_" + newTrackInstanceIdx.toString());
																			editTrack(track.id, newTrackId, trackName);
																		}}
																		customTrigger={
																			<div
																				className="h-10 text-white rounded flex items-center justify-center cursor-pointer"
																			>
																				<EditIcon style={{
																					width: "14px", 
																					height: "14px"
																				}}/>
																			</div>
																		}
																	/>
																</div>
															)}

																			{/* Delete button - for all tracks */}
																			{!isViewMode && (
																				<div className="absolute top-[10px] right-2 text-sm"
																						 onPointerDown={e => e.stopPropagation()}>
																					<button
																						onClick={(e) => handleClearOrDeleteTrackPopoverOpen(e, track.id)}
																						className={track.marked_for_deletion ? "text-gray-500 hover:text-gray-600" : "text-red-500 hover:text-red-700"}
																						title="Clear Track"
																					>
																						<BsTrash className="w-4 h-4"/>
																					</button>
																				</div>
																			)}

																			{/* Track Name - Horizontal Layout */}
																			<div className="flex items-center h-full px-2 py-2 gap-2">
																				{/* Icon on the left - taking two lanes */}
																				<div
																					className="flex items-center justify-center flex-shrink-0"
																					style={{
																						fontSize: `${Math.max(18, 22 * Math.min(verticalZoomLevel, 0.9))}px`,
																						width: `${Math.max(32, 40 * Math.min(verticalZoomLevel, 0.9))}px`,
																						height: `${Math.max(32, 40 * Math.min(verticalZoomLevel, 0.9))}px`,
																					}}
																				>
																					{(() => {
																						const trackName = track.name || 'Unknown Track';
																						const isUnknown = isUnknownTrack(trackName);
																						const isMarkedForDeletion = track.marked_for_deletion;
																						
																						if (isMarkedForDeletion) {
																							return (
																								<span
																									style={{
																										color: "#6b7280", 
																										fontWeight: "100", 
																										fontSize: `${Math.max(28, 36 * Math.min(verticalZoomLevel, 0.9))}px`, 
																									}}
																								>
																									×
																								</span>
																							);
																						} else {
																							const icon = getDynamicIcon(trackName.split("(")[0]);
																							if (icon === "!") {
																								return (
																									<span
																										style={{
																											color: "#dc2626", // red-600
																											fontWeight: "900", // extra bold
																											fontSize: `${Math.max(20, 26 * Math.min(verticalZoomLevel, 0.9))}px`, // slightly larger
																										}}
																									>
																										!
																									</span>
																								);
																							}
																							return icon;
																						}
																					})()}
																				</div>
																				
																				{/* Text on the right */}
																				<div className="flex flex-col justify-center flex-1 min-w-0">
																					{(() => {
																						const trackName = track.name || 'Unknown Track';
																						const isUnknown = isUnknownTrack(trackName);
																						const isMarkedForDeletion = track.marked_for_deletion;
																						
																						if (isMarkedForDeletion) {
																							return (
																								<>
																									{/* English Text */}
																									<div 
																										className="text-left mb-1"
																										style={{
																											fontSize: `${Math.max(11, 14 * Math.min(verticalZoomLevel, 0.9))}px`,
																											lineHeight: "1.2",
																											fontWeight: "600",
																											color: "#6b7280", // gray-500
																											overflow: "hidden",
																											textOverflow: "ellipsis",
																											whiteSpace: "nowrap",
																										}}
																										title="Marked for Deletion"
																									>
																										Marked for Deletion
																									</div>
																									
																									{/* Chinese Text */}
																									<div 
																										className="text-left"
																										style={{
																											fontSize: `${Math.max(10, 12 * Math.min(verticalZoomLevel, 0.9))}px`,
																											lineHeight: "1.2",
																											fontWeight: "500",
																											color: "#6b7280", // gray-500
																											overflow: "hidden",
																											textOverflow: "ellipsis",
																											whiteSpace: "nowrap",
																										}}
																										title="标记为应删除乐轨"
																									>
																										标记为应删除乐轨
																									</div>
																								</>
																							);
																						} else if (isUnknown) {
																							return (
																								<>
																									{/* English Text */}
																									<div 
																										className="text-left mb-1"
																										style={{
																											fontSize: `${Math.max(11, 14 * Math.min(verticalZoomLevel, 0.9))}px`,
																											lineHeight: "1.2",
																											fontWeight: "600",
																											color: "#dc2626", // red-600
																											overflow: "hidden",
																											textOverflow: "ellipsis",
																											whiteSpace: "nowrap",
																										}}
																										title="Unknown Instrument"
																									>
																										Unknown Instrument
																									</div>
																									
																									{/* Chinese Text */}
																									<div 
																										className="text-left"
																										style={{
																											fontSize: `${Math.max(10, 12 * Math.min(verticalZoomLevel, 0.9))}px`,
																											lineHeight: "1.2",
																											fontWeight: "500",
																											color: "#dc2626", // red-600
																											overflow: "hidden",
																											textOverflow: "ellipsis",
																											whiteSpace: "nowrap",
																										}}
																										title="请选择乐器"
																									>
																										请选择乐器
																									</div>
																								</>
																							);
																						} else {
																							return (
																								<>
																									{/* English Text */}
																									<div 
																										className="text-left mb-1"
																										style={{
																											fontSize: `${Math.max(11, 14 * Math.min(verticalZoomLevel, 0.9))}px`,
																											lineHeight: "1.2",
																											fontWeight: "600",
																											overflow: "hidden",
																											textOverflow: "ellipsis",
																											whiteSpace: "nowrap",
																										}}
																										title={trackName.split("(")[0].trim()}
																									>
																										{trackName.split("(")[0].trim()}
																									</div>
																									
																									{/* Chinese Text */}
																									{(() => {
																										const chineseMatch = trackName.match(/\(([^)]+)\)/);
																										const chineseText = chineseMatch ? chineseMatch[1] : '';
																										return chineseText ? (
																											<div 
																												className="text-left"
																												style={{
																													fontSize: `${Math.max(10, 12 * Math.min(verticalZoomLevel, 0.9))}px`,
																													lineHeight: "1.2",
																													fontWeight: "500",
																													color: "#666",
																													overflow: "hidden",
																													textOverflow: "ellipsis",
																													whiteSpace: "nowrap",
																												}}
																												title={chineseText}
																											>
																												{chineseText}
																											</div>
																										) : null;
																									})()}
																								</>
																							);
																						}
																					})()}
																				</div>
																			</div>
																		</div>
																	</div>
																</div>
															</div>

															<>
																{track.marked_for_deletion ? (
																	<div className="flex-1 flex items-center justify-center p-2 font-semibold rounded">
																		<Typography className="text-gray-600 text-sm font-medium">
																			⚠ This track is marked for deletion
																		</Typography>
																	</div>
																) : track.unlocked ? isUnknownTrack(track.name) ? (
																	<div className="flex-1 flex items-center justify-center p-2 font-semibold rounded">
																		<Typography className="text-red-800 text-sm font-medium">
																			⚠ Select an instrument with ✎ button for this unknown track before annotating this
																			track
																		</Typography>
																	</div>
																) : (
																	<div className="flex-1" style={{overflow: 'hidden'}}
																			onPointerDown={e => e.stopPropagation()}>
																		<AnnotationLayer
																			trackId={track.id}
																			annotations={getTrackAnnotations(track.id)}
																			timeSignatures={timeSignatures}
																			beatsPerMeasureMap={beatsPerMeasureMap}
																			measures={generalInfo.duration_number_of_bars}
																			zoomLevel={dynamicZoomValue}
																			height={120 * verticalZoomLevel}
																			collapsed={track.collapsed}
																			onAnnotationCreate={handleAnnotationCreate}
																			onAnnotationUpdate={handleAnnotationUpdate}
																			onAnnotationSelect={handleAnnotationSelect}
																			onAnnotationDelete={handleAnnotationDeleteWithSelection}
																			onAnnotationResize={handleAnnotationResize}
																			annotationFormAttributes={track.annotation_form_attributes}
																							sharedScrollContainer={sharedScrollContainer}
																			className="annotation-track-layer"
																			trackColor={track.colorType}
																							sharedGridLevel={sharedGridLevel}
																							isSelected={track.id === selectedTrackId}
																							isMarkedForDeletion={track.marked_for_deletion}
																							toolMode={editorInteraction.toolMode}
																							selectedIds={clipboardSelection.trackId === track.id ? clipboardSelection.ids : []}
																			copiedSourceIds={getClipboardPresentation(editorInteraction, track.id).copiedSourceIds}
																							pastedIds={clipboardSelection.trackId === track.id ? editorInteraction.pastedIds : []}
																			isClipboardTrack={getClipboardPresentation(editorInteraction, track.id).isClipboardTrack}
																							onSelectionChange={handleSelectionChange}
																							onBlockSelectionRequest={handleBlockSelectionRequest}
																							onClipboardContextMenu={handleClipboardContextMenu}
																							onSelectionGeometry={handleSelectionGeometry}
																							onAnnotationsMove={handleAnnotationsMove}
																						/>
																	</div>
																) : (
																	<div
																		className="flex-1 flex flex-col justify-center items-start pl-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm">
																		<Typography className="text-gray-800 text-sm font-medium">
																			⚠ Select a Chord Labelling System before annotating the Chord Track
																		</Typography>
																		<div className="mt-2 w-full">
																			<MultipleChoice
																				mandatory={true}
																				options={ChordLabellingSystemOptions}
                                        instruction={ChordLabellingSystemInstruction}
																				singleSelect={true}
																				questionText={"Chord Labelling System"}
																				allowAnyLevelSelection={true}
																				onSelect={(selectedItem) => {
																					if (selectedItem && selectedItem.length > 0) {
																						unlockChordTrack(selectedItem[0]);
																					}
																				}}
																				className="w-full border border-gray-400 rounded-md p-2 bg-white hover:bg-gray-50 transition duration-200"
																			/>
																		</div>
																	</div>
																)}
															</>
														</>
													)}
												</div>
											</SortableTrack>
										))}
									</div>
								</SortableContext>
							</DndContext>
						</div>
						{/* </ContextMenuWrapper> */}

						{Boolean(
								!isViewMode && !isTrackEditMode &&
								editorInteraction.toolMode === 'cursor' &&
								clipboardSelection.ids.length > 0 &&
								editorInteraction.copyPopover
							) && (
							<Box sx={{
								position: 'fixed',
								left: editorInteraction.copyPopover.left,
								top: editorInteraction.copyPopover.top,
								transform: 'translateY(-50%)',
								zIndex: 1200,
								borderRadius: '9px',
								border: '1px solid #d4d8de',
								boxShadow: '0 8px 22px rgba(28,34,44,.18)',
								overflow: 'hidden',
								backgroundColor: '#fff',
							}}>
							<Button
								startIcon={<ContentCopyOutlinedIcon />}
								onClick={handleCopy}
								sx={{
									px: 1.6,
									py: 0.9,
									color: '#20242a',
									fontSize: 14,
									fontWeight: 700,
									textTransform: 'none',
									borderRadius: 0,
								}}
							>
								Copy (复制)
							</Button>
							</Box>
						)}

						<AnnotationClipboardMenu
							anchorPosition={editorInteraction.contextMenu?.position || null}
							canCopy={Boolean(
								clipboardSelection.ids.length &&
								clipboardSelection.trackId === editorInteraction.contextMenu?.trackId
							)}
							canPaste={getCanPaste(editorInteraction, editorInteraction.contextMenu?.trackId)}
							canDelete={Boolean(
								clipboardSelection.ids.length &&
								clipboardSelection.trackId === editorInteraction.contextMenu?.trackId
							)}
							deleteCount={clipboardSelection.ids.length}
							onCopy={handleCopy}
							onPaste={() => openPasteDialog(editorInteraction.contextMenu?.trackId)}
							onDelete={openDeleteDialog}
							onClose={() => dispatchEditorInteraction({ type: 'CLOSE_CONTEXT_MENU' })}
						/>

						<AnnotationBatchDeleteDialog
							open={Boolean(deleteDialogSelection)}
							count={deleteDialogSelection?.ids.length || 0}
							onConfirm={confirmDeleteSelection}
							onCancel={() => setDeleteDialogSelection(null)}
						/>

						<AnnotationPasteDialog
							open={pasteDialogOpen}
							error={pasteError}
							onChoose={handlePasteChoice}
							onClose={() => {
								setPasteDialogOpen(false);
								setPasteError(null);
							}}
							onAcknowledge={() => {
								setPasteDialogOpen(false);
								setPasteError(null);
							}}
						/>

						{/* Time Signature Selector */}
						{showTimeSignatureSelector && (
							<TimeSignatureSelector
								measureIndex={activeMeasure}
								currentSignature={timeSignatures[activeMeasure - 1]}
								onSelect={handleTimeSignatureChange}
								onCancel={handleCloseSelector}
								position={selectorPosition}
							/>
						)}

						{/* Selected Annotation Details */}
						{selectedAnnotationId && (
							<div style={{border: '1px solid #000', padding: '10px', marginBottom: '20px'}}>
								<h3>Selected Annotation</h3>
								<pre>
                    {JSON.stringify(
											annotations.find(a => a.id === selectedAnnotationId),
											null,
											2
										)}
                  </pre>
							</div>
						)}
						<div/>
						<Popover
							open={Boolean(clearOrDeleteTrackAnchorEl)}
							anchorEl={clearOrDeleteTrackAnchorEl}
							onClose={handleClearOrDeleteTrackPopoverClose}
							anchorOrigin={{
								vertical: 'top',
								horizontal: 'right',
							}}
							transformOrigin={{
								vertical: 'top',
								horizontal: 'left',
							}}
							disableEnforceFocus
							disableAutoFocus
						>
							<div className="p-4 w-96 bg-white shadow-md rounded space-y-2">
								{(() => {
									const currentTrack = tracks.find(t => t.id === clearOrDeleteTrackId);
									const isMarkedForDeletion = currentTrack?.marked_for_deletion;
									
									if (isMarkedForDeletion) {
										// Show ONLY unmark option for tracks marked for deletion
										return (
											<button
												onClick={() => {
													setTrackToToggleDeletion(currentTrack);
													setTrackDeletionDialogOpen(true);
													handleClearOrDeleteTrackPopoverClose();
												}}
												className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded"
											>
												Unmark this track for deletion
												<br />
												<span className="font-semibold">(取消将该乐轨标记为应删除乐轨)</span>
											</button>
										);
									} else {
										// Show all options for normal tracks
										return (
											<>
												<button
													onClick={() => {
														clearOrRemoveTrack(true);
													}}
													className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded"
												>
													Clear Track 
													<br /> 
													<span className="font-semibold">(清空轨道)</span>
												</button>
												{clearOrDeleteTrackId && clearOrDeleteTrackId !== 'chord-track' && !clearOrDeleteTrackId.includes('music-structure') && (
													<>
														<hr />
														<button
															onClick={() => {
																setTrackToToggleDeletion(currentTrack);
																setTrackDeletionDialogOpen(true);
																handleClearOrDeleteTrackPopoverClose();
															}}
															className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded"
														>
															Mark this track for deletion
															<br />
															<span className="font-semibold">(将该乐轨标记为应删除乐轨)</span>
														</button>
														<hr />
														<button
															onClick={() => {
																clearOrRemoveTrack(false);
															}}
															className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded"
															style={{pointerEvents: !isAdminMode ? "none" : (!isTrackEditMode ? "none" : "auto"), opacity: !isAdminMode ? 0.5 : (!isTrackEditMode ? 0.5 : 1)}}
														>
															Clear Track and Delete Instrument
															<br />
															<span className="font-semibold">(清空轨道并删除该乐器)</span>
														</button>
													</>
												)}
											</>
										);
									}
								})()}
							</div>
						</Popover>
					</Collapse>
				</div>
			</div>

			{/* Track Deletion Confirmation Dialog */}
			<TrackDeletionConfirmDialog
				open={trackDeletionDialogOpen}
				onClose={() => {
					setTrackDeletionDialogOpen(false);
					setTrackToToggleDeletion(null);
				}}
				onConfirm={() => {
					if (trackToToggleDeletion) {
						toggleTrackForDeletion(trackToToggleDeletion.id);
						setTrackToToggleDeletion(null);
					}
				}}
				trackName={trackToToggleDeletion?.name}
				isMarkedForDeletion={trackToToggleDeletion?.marked_for_deletion || false}
			/>

			{/* Instrument Change Confirmation Dialog */}
			<InstrumentChangeConfirmDialog
				open={instrumentChangeDialog.open}
				onClose={handleInstrumentChangeCancel}
				onConfirm={handleInstrumentChangeConfirm}
				changeType={instrumentChangeDialog.changeType}
				oldInstrumentName={instrumentChangeDialog.oldInstrumentName}
				newInstrumentName={instrumentChangeDialog.newInstrumentName}
			/>

			{/* Submit Success Modal */}
			<Dialog
				open={submitModalOpen}
				onClose={() => setSubmitModalOpen(false)}
				PaperProps={{
					sx: {
						borderRadius: '12px',
						padding: '24px',
						minWidth: '300px',
						textAlign: 'center',
						position: 'relative',
						maxWidth: '400px',
					}
				}}
			>
				{/* Close button */}
				<Button
					onClick={() => setSubmitModalOpen(false)}
					sx={{
						position: 'absolute',
						top: '8px',
						right: '8px',
						minWidth: '32px',
						width: '32px',
						height: '32px',
						borderRadius: '50%',
						color: '#9ca3af',
						padding: 0,
						'&:hover': {
							backgroundColor: '#f3f4f6',
							color: '#6b7280',
						}
					}}
				>
					<svg 
						width="18" 
						height="18" 
						viewBox="0 0 24 24" 
						fill="none" 
						stroke="currentColor" 
						strokeWidth="2"
						strokeLinecap="round" 
						strokeLinejoin="round"
					>
						<line x1="18" y1="6" x2="6" y2="18"></line>
						<line x1="6" y1="6" x2="18" y2="18"></line>
					</svg>
				</Button>

				<DialogContent sx={{ 
					display: 'flex', 
					flexDirection: 'column', 
					alignItems: 'center',
					padding: '16px 24px !important',
					paddingTop: '24px !important',
				}}>
					{/* Green checkmark icon */}
					<div 
						style={{
							width: '48px',
							height: '48px',
							backgroundColor: '#22c55e',
							borderRadius: '50%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							marginBottom: '16px',
						}}
					>
						<svg 
							width="24" 
							height="24" 
							viewBox="0 0 24 24" 
							fill="none" 
							stroke="white" 
							strokeWidth="3"
							strokeLinecap="round" 
							strokeLinejoin="round"
						>
							<path d="M20 6L9 17l-5-5"/>
						</svg>
					</div>
					
					<Typography 
						variant="h6" 
						sx={{ 
							fontWeight: 'bold',
							color: '#1f2937',
							marginBottom: 0,
						}}
					>
						Successfully Submitted (提交成功)!
					</Typography>
				</DialogContent>
			</Dialog>
		</div>{accessDialog}</>
	)
}

export default AnnotationSheet;
